import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MarqueeTicker from '../components/MarqueeTicker';
import { 
  Gavel, Search, MessageSquare, ShieldCheck, LogOut, LayoutDashboard, 
  Award, Shield, BookOpen, Clock, ChevronRight, UserCheck, Scale, ChevronDown,
  X, ExternalLink, Play, Video, PictureInPicture, Sparkles, FileText, Landmark,
  Layers, ArrowUpRight, CheckCircle2, Bookmark, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { BlacksLawDictionaryModal } from '../components/BlacksLawDictionaryModal';
import { DocumentDetailModal } from '../components/DocumentDetailModal';
import ChatSystem from '../components/ChatSystem';
import LawDirectory from './LawDirectory';
import SmartLegalDrafting from './SmartLegalDrafting';
import AiAssistant from './AiAssistant';
import ElumVideoConferenceModal from '../components/ElumVideoConferenceModal';

const LandingPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { themeMode, setThemeMode } = useTheme();
  const navigate = useNavigate();
  
  // State for hover animations and dropdowns
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // LANGUAGE (EN/UR) STATE
  const [language, setLanguage] = useState<'EN' | 'UR'>('EN');

  // Live Chat Access Modal & Window-within-Window State
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showLiveChatWindow, setShowLiveChatWindow] = useState(false);
  // Law Dictionary Modal State
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  // Law Directory Modal State
  const [showLawDirectoryModal, setShowLawDirectoryModal] = useState(false);
  // Smart Legal Drafting Modal State
  const [showSmartDraftingModal, setShowSmartDraftingModal] = useState(false);
  // AI Legal Assistant Modal State
  const [showAiAssistantModal, setShowAiAssistantModal] = useState(false);
  // Elum Meet (Video Conference & PiP) Modal State
  const [showVideoConferenceModal, setShowVideoConferenceModal] = useState(false);

  // Document Detail Modal State for Direct Case / Statute Inspection
  const [selectedLandingDoc, setSelectedLandingDoc] = useState<any | null>(null);

  // Latest Cases & Statutes Stream State
  const [latestCases, setLatestCases] = useState<any[]>([
    {
      id: 'case_sc_2026_142',
      title: 'Federation of Pakistan v. Sunni Ittehad Council & Others',
      court: 'Supreme Court of Pakistan',
      citationNumber: '2026 SCMR 142',
      year: '2026',
      jurisdiction: 'Federal',
      category: 'Constitutional Law',
      judgment_text: 'Held: Allocation of proportional reserved seats under Articles 51 and 106 of the Constitution of Islamic Republic of Pakistan, 1973 must be strictly adjudicated based on political party registration and timely candidate declarations.'
    },
    {
      id: 'case_sc_2026_88',
      title: 'Syed Yousaf Raza Gillani v. Speaker National Assembly',
      court: 'Supreme Court of Pakistan',
      citationNumber: 'PLD 2026 SC 88',
      year: '2026',
      jurisdiction: 'Federal',
      category: 'Constitutional / Contempt',
      judgment_text: 'Constitutional scope of judicial review under Article 184(3) in matters pertaining to executive compliance, parliamentary privileges, and contempt of court jurisprudence.'
    },
    {
      id: 'case_lhc_2026_110',
      title: 'Mst. Kaneez Fatima v. Muhammad Shafi & Others',
      court: 'Lahore High Court',
      citationNumber: '2026 CLD 110',
      year: '2026',
      jurisdiction: 'Punjab',
      category: 'Family & Property Law',
      judgment_text: 'Enforcement of dower obligations and inheritance rights under Family Courts Act 1964; protection of women co-sharers in ancestral agricultural property.'
    },
    {
      id: 'case_shc_2026_205',
      title: 'M/s K-Electric Limited v. Federation of Pakistan & NEPRA',
      court: 'High Court of Sindh',
      citationNumber: '2026 CLD 205',
      year: '2026',
      jurisdiction: 'Sindh',
      category: 'Corporate & Regulatory Law',
      judgment_text: 'Judicial scrutiny of NEPRA tariff determination under Regulation of Generation, Transmission and Distribution of Electric Power Act 1997.'
    },
    {
      id: 'case_ihc_2025_405',
      title: 'Imran Ahmad Khan Niazi v. State through National Accountability Bureau',
      court: 'Islamabad High Court',
      citationNumber: '2025 PCrLJ 405',
      year: '2025',
      jurisdiction: 'Islamabad',
      category: 'Criminal & NAB Law',
      judgment_text: 'Principles governing grant of post-arrest bail under Section 9 of National Accountability Ordinance 1999 read with Code of Criminal Procedure 1898.'
    },
    {
      id: 'case_bhc_2025_992',
      title: 'Haji Muhammad Aslam v. Government of Balochistan',
      court: 'High Court of Balochistan',
      citationNumber: '2025 MLD 992',
      year: '2025',
      jurisdiction: 'Balochistan',
      category: 'Land Acquisition & Civil Law',
      judgment_text: 'Land Acquisition Act 1894, determination of market compensation for highway corridors and mineral concession rights.'
    },
    {
      id: 'case_shc_2025_1430',
      title: 'Dr. Farooq Sattar v. Province of Sindh & Others',
      court: 'High Court of Sindh',
      citationNumber: '2025 YLR 1430',
      year: '2025',
      jurisdiction: 'Sindh',
      category: 'Local Government Law',
      judgment_text: 'Sindh Local Government Act 2013, constitutional mandate of devolution of financial and administrative authority under Article 140-A.'
    },
    {
      id: 'case_phc_2025_1180',
      title: 'State through Advocate General KPK v. Zar Gul Khan',
      court: 'Peshawar High Court',
      citationNumber: '2025 PCrLJ 1180',
      year: '2025',
      jurisdiction: 'KPK',
      category: 'Anti-Terrorism & Criminal Law',
      judgment_text: 'Section 7 of Anti-Terrorism Act 1997, evidentiary standard of modern forensic recordings and call detail record (CDR) admissibility.'
    }
  ]);

  const [latestStatutes, setLatestStatutes] = useState<any[]>([
    {
      id: 'statute_fed_2026_cyber',
      title: 'National Digital Forensics & Cyber Security Authority Act 2026',
      name: 'National Digital Forensics & Cyber Security Authority Act 2026',
      jurisdiction: 'Federal',
      issuing_authority: 'Parliament of Pakistan',
      legislation_type: 'Act No. XXIV of 2026',
      year: '2026',
      total_sections_count: 38,
      total_word_count: 14500,
      short_description: 'An Act to establish a unified National Digital Forensics & Cyber Security Authority for critical national information infrastructure protection.'
    },
    {
      id: 'statute_fed_2026_crim',
      title: 'Pakistan Criminal Law Amendment (Modern Evidentiary Procedures) Act 2026',
      name: 'Pakistan Criminal Law Amendment (Modern Evidentiary Procedures) Act 2026',
      jurisdiction: 'Federal',
      issuing_authority: 'Parliament of Pakistan',
      legislation_type: 'Act No. XVII of 2026',
      year: '2026',
      total_sections_count: 52,
      total_word_count: 21200,
      short_description: 'Comprehensive amendment introducing mandatory digital audio-video recording of trial proceedings and chain of custody biometric verification.'
    },
    {
      id: 'statute_punjab_2025_adr',
      title: 'Punjab Civil Procedure (Alternative Dispute Resolution) Amendment Act 2025',
      name: 'Punjab Civil Procedure (Alternative Dispute Resolution) Amendment Act 2025',
      jurisdiction: 'Punjab',
      issuing_authority: 'Punjab Provincial Assembly',
      legislation_type: 'Act No. XII of 2025',
      year: '2025',
      total_sections_count: 26,
      total_word_count: 9800,
      short_description: 'Mandatory pre-trial ADR mediation and court-annexed reconciliation procedures in commercial and tenancy disputes.'
    },
    {
      id: 'statute_sindh_2025_water',
      title: 'Sindh Protection of Agricultural Tenancy & Water Rights Act 2025',
      name: 'Sindh Protection of Agricultural Tenancy & Water Rights Act 2025',
      jurisdiction: 'Sindh',
      issuing_authority: 'Sindh Provincial Assembly',
      legislation_type: 'Act No. IX of 2025',
      year: '2025',
      total_sections_count: 34,
      total_word_count: 12600,
      short_description: 'Protection of agricultural tenants against unlawful ejectment, watercourse allocation transparency, and fair crop valuation tribunals.'
    },
    {
      id: 'statute_kpk_2025_gov',
      title: 'Khyber Pakhtunkhwa E-Governance & Right to Public Services Act 2025',
      name: 'Khyber Pakhtunkhwa E-Governance & Right to Public Services Act 2025',
      jurisdiction: 'KPK',
      issuing_authority: 'KPK Provincial Assembly',
      legislation_type: 'Act No. XXI of 2025',
      year: '2025',
      total_sections_count: 29,
      total_word_count: 10400,
      short_description: 'Statutory guarantees for timely digital delivery of public certificates, land deeds, and administrative licenses with citizen compensation.'
    },
    {
      id: 'statute_bal_2025_mines',
      title: 'Balochistan Minerals & Mines (Value Addition & Export) Act 2025',
      name: 'Balochistan Minerals & Mines (Value Addition & Export) Act 2025',
      jurisdiction: 'Balochistan',
      issuing_authority: 'Balochistan Provincial Assembly',
      legislation_type: 'Act No. V of 2025',
      year: '2025',
      total_sections_count: 44,
      total_word_count: 18900,
      short_description: 'Regulatory framework for mineral exploration leases, mandatory domestic refining, and dedicated royalty funds for local community welfare.'
    },
    {
      id: 'statute_fed_2026_sro108',
      title: 'Federal Board of Revenue SRO 108(I)/2026 (Customs Digital Valuation)',
      name: 'Federal Board of Revenue SRO 108(I)/2026 (Customs Digital Valuation)',
      jurisdiction: 'Federal',
      issuing_authority: 'Federal Board of Revenue (FBR)',
      legislation_type: 'SRO 108(I)/2026',
      year: '2026',
      total_sections_count: 12,
      total_word_count: 5300,
      short_description: 'Statutory regulatory order prescribing digital valuation metrics and automated HS-code tariff clearance procedures.'
    },
    {
      id: 'statute_ajk_2025_bar',
      title: 'Azad Jammu & Kashmir Legal Practitioners & Bar Councils Act 2025',
      name: 'Azad Jammu & Kashmir Legal Practitioners & Bar Councils Act 2025',
      jurisdiction: 'Azad Jammu & Kashmir',
      issuing_authority: 'AJK Legislative Assembly',
      legislation_type: 'Act No. XIV of 2025',
      year: '2025',
      total_sections_count: 48,
      total_word_count: 17200,
      short_description: 'Consolidated statutory enactments governing bar council elections, disciplinary tribunals, and advocate enrollment qualifications in AJK.'
    }
  ]);

  // Fetch live latest cases & statutes on mount
  useEffect(() => {
    api.get('/documents?limit=8&sortBy=year').then(res => {
      if (res.data && Array.isArray(res.data.documents) && res.data.documents.length > 0) {
        setLatestCases(res.data.documents);
      }
    }).catch(() => {});

    api.get('/statutes?limit=8&sortBy=year').then(res => {
      if (res.data && Array.isArray(res.data.statutes) && res.data.statutes.length > 0) {
        setLatestStatutes(res.data.statutes);
      }
    }).catch(() => {});
  }, []);

  // Inject Google Fonts for Playfair Display and Inter & Set Document Title
  useEffect(() => {
    document.title = "Elum Law Associates — Pakistan's AI-Powered Judicial Database & Legal Repository";
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Noto+Nastaliq+Urdu:wght@400;700&family=Playfair+Display:ital,wght@0,600;0,700;0,800;0,900;1,600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Update theme mode in localStorage
  useEffect(() => {
    localStorage.setItem('theme_mode', themeMode);
  }, [themeMode]);

  // Listen for global openAccessModal and openVideoConference events
  useEffect(() => {
    const handleOpenAccessModal = () => {
      setShowAccessModal(true);
    };
    const handleOpenVideoConference = () => {
      setShowVideoConferenceModal(true);
    };
    window.addEventListener('openAccessModal', handleOpenAccessModal);
    window.addEventListener('openVideoConference', handleOpenVideoConference);
    return () => {
      window.removeEventListener('openAccessModal', handleOpenAccessModal);
      window.removeEventListener('openVideoConference', handleOpenVideoConference);
    };
  }, []);

  // Dynamic Marquee 1 & Marquee 2 items managed at Superuser & DB Admin disposal
  const [marquee1Items, setMarquee1Items] = useState<any[]>([]);
  const [marquee2Items, setMarquee2Items] = useState<any[]>([]);
  const [activeMarqueeDetail, setActiveMarqueeDetail] = useState<{
    type: 'case' | 'event' | 'news';
    title: string;
    citation?: string;
    court?: string;
    subject?: string;
    held?: string;
    summary?: string;
    subtitle?: string;
    description?: string;
    highlights?: string[];
    imageUrl?: string;
    videoUrl?: string;
    linkUrl?: string;
  } | null>(null);

  const CASE_DETAILS_MAP: Record<string, any> = {
    'PLD 2026 SC 101': {
      title: 'PLD 2026 Supreme Court 101',
      citation: 'PLD 2026 Supreme Court 101',
      court: 'Constitutional Benches, Supreme Court of Pakistan',
      subject: '26th Constitutional Amendment Reference and Division of Judiciary Benches.',
      held: 'The division of judicial work between appellate benches and constitutional benches is fully constitutional. Litigations relating to interpretation of laws and executive powers fall under the exclusive purview of the constitutional benches header presided over by the Chief Justice of the FCC.',
      summary: 'Under the revised structures, judicial digitization records remain unified. Precedents drafted via automated legal intelligence shall have advisory weight when certified by a senior High Court Advocate chambers.',
      linkUrl: '/cases/pld-2026-sc-101.html',
    },
    'SCMR 2025 845': {
      title: 'SCMR 2025 Supreme Court 845',
      citation: 'SCMR 2025 845',
      court: 'Appellate Jurisdiction, Supreme Court of Pakistan',
      subject: 'Civil Procedure and Electronic Invoicing Enforcements.',
      held: 'The application of digital tracking for filing fees and royalty logs is held valid. All legal firms must record standard billing metrics in local currency (PKR).',
      summary: 'Invoicing and finance systems synchronized under regional networks fall within secure transaction protection frameworks.',
      linkUrl: '/cases/scmr-2025-845.html',
    },
    'PLD 2025 BHC 72': {
      title: 'PLD 2025 Balochistan High Court 72',
      citation: 'PLD 2025 BHC 72',
      court: 'Division Bench, Balochistan High Court',
      subject: 'Judicial Digitization and Court Records Verification in Quetta.',
      held: 'The deployment of digital portal logs for tracking litigation files and case timelines is mandatory. Physical copy archives shall be supplemented with cloud storage nodes.',
      summary: 'Provincial High Court bench mandates integration of automated case file registries and cryptographic verification signatures across all district registries.',
      linkUrl: '/cases/pld-2025-bhc-72.html',
    },
    'CLC 2026 Karachi 190': {
      title: 'CLC 2026 Sindh High Court 190',
      citation: 'CLC 2026 Karachi 190',
      court: 'Sindh High Court, Karachi Bench',
      subject: 'Corporate Mergers & Regulatory Frameworks.',
      held: 'Validation of tech-driven royalty logs and financial audits. Automated micro-royalties allocated to firm founders are protected corporate property.',
      summary: 'Corporate dispute adjudication upholding digital revenue sharing matrices under Pakistani corporate law.',
      linkUrl: '/cases/clc-2026-karachi-190.html',
    },
    'PLD 2026 SC 55': {
      title: 'PLD 2026 Supreme Court 55',
      citation: 'PLD 2026 SC 55',
      court: 'Supreme Court of Pakistan, Full Bench',
      subject: 'AI-Assisted Automated Judgment Guidelines & Legal Tech Standards.',
      held: 'Legal tech nodes and automated case precedent indexing systems provide admissible assistance in High Court practice provided facts are verified.',
      summary: 'Guidelines establishing the admissibility and integrity of AI legal search repositories in Pakistani superior courts.',
      linkUrl: '/cases/pld-2026-sc-55.html',
    }
  };

  const EVENT_DETAILS_MAP: Record<string, any> = {
    'e1': {
      title: 'Chief Justice of Pakistan Oath Ceremony & Felicitation',
      subtitle: 'Felicitation & Historic Judicial Transition at Aiwan-e-Sadr',
      imageUrl: '/Justice_Yahya_Afridi_CJP.jpeg',
      description: 'Felicitation Ceremony of the 30th Chief Justice of Pakistan, Hon\'ble Mr. Justice Yahya Afridi. Supreme Court practitioners, provincial Chief Justices, federal ministers, and legal associates attended the event at Aiwan-e-Sadr.',
      highlights: [
        'Nationwide Case Management & Judicial Automation System',
        'Resolution of Civil & Constitutional Backlog Priorities',
        'High-Definition Live Courtroom Streaming Rollout',
        'Supreme Court & High Court Bar Council Coordination Protocol'
      ],
      linkUrl: '/events/cjp-oath.html',
    },
    'e2': {
      title: 'Federal Constitutional Court Head Inauguration Event',
      subtitle: 'Oath Ceremony of 1st Chief Justice of the FCC',
      imageUrl: '/Justice-Amin-ud-Din-Khan-CJ-FCC.jpeg',
      description: 'Formal Oath Ceremony marking the historical appointment of Hon\'ble Mr. Justice Amin-ud-Din Khan as the 1st Chief Justice of the Federal Constitutional Court of Pakistan.',
      highlights: [
        'Establishment of Dedicated Fundamental Rights Benches',
        'Separation of Appellate and Constitutional Roster Channels',
        'Provincial Constitutional Registry Interconnectivity',
        'Constitutional Bench High Court Jurisdiction Guidelines'
      ],
      linkUrl: '/events/fcc-inauguration.html',
    },
    'e3': {
      title: 'High Court of Balochistan Legal Digitization & e-Filing Rollout',
      subtitle: 'Modern Digital Courtrooms & Electronic Registers Inauguration',
      imageUrl: '/justice-muhammad-kamran-mulakhail-CJ_BHC.jpg',
      description: 'Unveiling of the brand new e-filing registers, digital cause list boards, and automated litigation assistants at the High Court of Balochistan, Quetta, chaired by Hon\'ble Chief Justice Muhammad Kamran Khan Mulakhail.',
      highlights: [
        'Instant Online Writ Petition & Roster Submissions',
        'Biometric Identification & Digital Notice Dispatch for Advocates',
        'Balochistan Bar Council AI Research Terminal Access',
        'Automated Cause List SMS & WhatsApp Alerts'
      ],
      linkUrl: '/events/bhc-automation.html',
    },
    'e4': {
      title: 'Elum Law Associates AI Legal Node & Database Launch',
      subtitle: 'Pakistan\'s Premier 229,000+ Case Law Repository Inauguration',
      imageUrl: '/owner.jpeg',
      description: 'Advocate NajeebUllah announces the deployment of Elum Law Associates\' advanced AI legal node, featuring 229,439 searchable judicial precedents, statutory acts matrix, and encrypted Bar & Bench chat network.',
      highlights: [
        'Smart Legal Drafting Studio with High Court Pleading Templates',
        'Judicial Voice Note Dictation & AI Transcription Engine',
        'Law Reporter Integration (PLD, SCMR, CLC, PCrLJ, MLD, PTD)',
        'AES-256 Encrypted Judicial Officer & Advocate Chat Node'
      ],
      linkUrl: '/events/elum-launch.html',
    }
  };

  const handleOpenCaseDetail = (item: any) => {
    const citationKey = Object.keys(CASE_DETAILS_MAP).find(k => 
      (item.title && item.title.includes(k)) || 
      (item.citation && item.citation.includes(k)) ||
      (item.linkUrl && item.linkUrl.includes(encodeURIComponent(k)))
    );

    if (citationKey && CASE_DETAILS_MAP[citationKey]) {
      setActiveMarqueeDetail({
        type: 'case',
        ...CASE_DETAILS_MAP[citationKey],
        title: CASE_DETAILS_MAP[citationKey].title || item.title,
      });
    } else {
      setActiveMarqueeDetail({
        type: 'case',
        title: item.title || 'Judicial Case Precedent',
        citation: item.citation || item.citationNumber || item.title,
        court: item.court || 'Superior Court of Pakistan',
        subject: item.subject || 'Constitutional & Appellate Precedent Report',
        held: item.held || item.description || 'Precedent published on live judicial repository.',
        summary: item.summary || item.content || 'Full precedent available in Elum Law Directory.',
        linkUrl: item.linkUrl || (item.citationNumber ? `/law-directory?query=${encodeURIComponent(item.citationNumber)}` : '#'),
      });
    }
  };

  const handleOpenEventDetail = (item: any) => {
    const eventKey = Object.keys(EVENT_DETAILS_MAP).find(k => 
      item._id === k || 
      (item.linkUrl && item.linkUrl.includes(k)) ||
      (item.title && item.title.toLowerCase().includes(k.toLowerCase())) ||
      (item.title && EVENT_DETAILS_MAP[k].title?.toLowerCase().includes(item.title.toLowerCase().slice(0, 8)))
    );

    if (eventKey && EVENT_DETAILS_MAP[eventKey]) {
      setActiveMarqueeDetail({
        type: 'event',
        ...EVENT_DETAILS_MAP[eventKey],
        title: EVENT_DETAILS_MAP[eventKey].title || item.title,
        imageUrl: item.imageUrl || EVENT_DETAILS_MAP[eventKey].imageUrl,
      });
    } else {
      setActiveMarqueeDetail({
        type: 'event',
        title: item.title || 'Judicial Event Broadcast',
        subtitle: item.subtitle || 'Live Event & Broadcast Transmission',
        imageUrl: item.imageUrl,
        videoUrl: item.videoUrl,
        description: item.description || item.subtitle || 'Judicial event covered under High Court media gallery.',
        highlights: item.highlights || [
          'Bar & Bench Proceedings Recorded',
          'High-Definition Courtroom Transmission Available',
          'High Court Circular & Media Roster Reference'
        ],
        linkUrl: item.linkUrl,
      });
    }
  };

  // Live Inventory Counts (Updated frequently every 15s)
  const [inventoryCounts, setInventoryCounts] = useState<{
    cases: number;
    statutes: number;
    dictionary: number;
    total: number;
    lastUpdated: string;
  }>({
    cases: 32021,
    statutes: 8669,
    dictionary: 7194,
    total: 47884,
    lastUpdated: new Date().toLocaleTimeString()
  });

  useEffect(() => {
    const fetchMarqueesAndInventory = async () => {
      try {
        const [res1, res2, statRes, dictRes, docRes] = await Promise.allSettled([
          api.get('/news/marquee1'),
          api.get('/news/marquee2'),
          api.get('/statutes?limit=1'),
          api.get('/dictionary/terms?limit=1'),
          api.get('/documents?limit=1')
        ]);

        if (res1.status === 'fulfilled' && Array.isArray(res1.value.data)) setMarquee1Items(res1.value.data);
        if (res2.status === 'fulfilled' && Array.isArray(res2.value.data)) setMarquee2Items(res2.value.data);

        let statuteCount = 8669;
        if (statRes.status === 'fulfilled' && statRes.value.data) {
          const fetched = statRes.value.data.totalCount || statRes.value.data.total || statRes.value.data.returnedCount;
          if (fetched && fetched > 10) {
            statuteCount = fetched;
          }
        }

        let dictCount = 7194;
        if (dictRes.status === 'fulfilled' && dictRes.value.data) {
          dictCount = dictRes.value.data.totalTerms || dictRes.value.data.totalCount || dictRes.value.data.total || 7194;
        }

        let caseCount = 32021;
        if (docRes.status === 'fulfilled' && docRes.value.data) {
          caseCount = docRes.value.data.totalCount || docRes.value.data.total || docRes.value.data.caseCount || 32021;
        }

        setInventoryCounts({
          cases: caseCount,
          statutes: statuteCount,
          dictionary: dictCount,
          total: caseCount + statuteCount + dictCount,
          lastUpdated: new Date().toLocaleTimeString()
        });
      } catch (err) {
        console.error('Error fetching marquees or inventory counts:', err);
      }
    };

    fetchMarqueesAndInventory();
    const interval = setInterval(fetchMarqueesAndInventory, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Translations dictionary
  const dict = {
    EN: {
      home: "Main Page",
      judiciary: "Judiciary",
      services: "Services",
      foundership: "Foundership",
      dashboard: "Dashboard",
      logout: "Logout",
      login: "Login",
      getStarted: "Get Started",
      subBadge: "Advocate NajeebUllah Protected Enterprise",
      heroTitle: "International Standard Legal Excellence in Pakistan",
      heroDesc: "The most sophisticated digital legal node bridging traditional litigation with full-scale artificial intelligence. End-to-end secure tunnels for corporate entities, firm affiliates, and judges.",
      heroBtn: "Register Your Case Portal",
      judiciarySub: "SUPREME CONSTITUTIONAL SUPREMACY",
      judiciaryTitle: "Honorable Judiciary of Pakistan",
      servicesSub: "HIGH-VALUE CAPABILITIES",
      servicesTitle: "AI-Driven Legal Infrastructure",
      founderSub: "FIRM PRINCIPAL & FOUNDER",
      founderTitle: "Advocate NajeebUllah",
      founderDesc: "Under the visionary foundership of Advocate NajeebUllah, Elum Law Associates Pakistan stands at the forefront of constitutional interpretation, appellate advocacy, and technology-driven legal solutions.",
      seniorAdv: "Senior Advocate of High Court Chambers",
      advisorGovt: "Advisor to Government Entities & Corporate Boards",
      reportedCases: "Reported Cases",
      recentEvents: "Recent Events",
      chatNode: "Live Chat Node",
      cjpBadge: "Supreme Court of Pakistan",
      cjpTitle: "Honorable Chief Justice Mr. Justice Yahya Afridi",
      cjpDesc: "Justice Yahya Afridi is the 30th and current Chief Justice of Pakistan. Born on January 23, 1965, he holds the historic distinction of being the first Chief Justice of an apex court in Pakistan to hail from the erstwhile Federally Administered Tribal Areas (FATA).",
      fccBadge: "Federal Constitutional Court",
      fccTitle: "Honorable Chief Justice Mr. Justice Amin-ud-Din Khan",
      fccDesc: "Justice Amin-ud-Din Khan as the Head of constitutional benches and Chief Justice of the Federal Constitutional Court. He also served as a justice of the Lahore High Court from 12 May 2011 to 22 October 2019. He was appointed by President of Pakistan on 13 November 2025 as 1st Chief Justice of Federal Constitutional Court of Pakistan.",
      bhcBadge: "Balochistan High Court",
      bhcTitle: "Honorable Chief Justice Mr. Justice M. Kamran Khan Mulakhail",
      bhcDesc: "Justice Muhammad Kamran Khan Mulakhail is the current Chief Justice of the Balochistan High Court (BHC), having assumed office in December 2025. A legal professional, he previously served as a BHC judge since August 2013 and has a strong background in constitutional law, leadership and judicial reforms.",
      desc1: "Instant search over thousands of Pakistani citations, Supreme Court judgments, acts, and SROs mapped via RAG.",
      desc2: "Input case briefs and secure immediate legal outcome predictions with probability stats and core justifications.",
      desc3: "Trace lawsuit stages from registration down to eventual disposition. View secure QR notifications.",
      desc4: "Collaborative workspace where High Court practitioners and associates draft joint opinions for govt units.",
    },
    UR: {
      home: "صفحہ اول",
      judiciary: "معزز عدلیہ",
      services: "قانونی خدمات",
      foundership: "بانی کا تعارف",
      dashboard: "ڈیش بورڈ",
      logout: "لاگ آؤٹ",
      login: "لاگ ان",
      getStarted: "شروع کریں",
      subBadge: "ایڈووکیٹ نجیب اللہ پروٹیکٹڈ انٹرپرائز",
      heroTitle: "پاکستان میں بین الاقوامی معیار کی قانونی مہارت",
      heroDesc: "روایتی قانونی چارہ جوئی کو مکمل مصنوعی ذہانت سے جوڑنے والا جدید ترین قانونی مرکز۔ کارپوریٹ اداروں، فرم کے ملحقہ اداروں اور معزز ججز کے لیے محفوظ ترین نیٹ ورک۔",
      heroBtn: "اپنا کیس پورٹل رجسٹر کریں",
      judiciarySub: "آئینی بالادستی اور خودمختاری",
      judiciaryTitle: "پاکستان کی معزز عدلیہ",
      servicesSub: "اعلیٰ ترین تکنیکی صلاحیتیں",
      servicesTitle: "اے آئی سے لیس قانونی ڈھانچہ",
      founderSub: "فرم کے پرنسپل اور بانی",
      founderTitle: "ایڈووکیٹ نجیب اللہ",
      founderDesc: "ایڈووکیٹ نجیب اللہ کی بصیرت انگیز قیادت میں، ایلم لا ایسوسی ایٹس پاکستان آئینی تشریح، اپیل کی وکالت، اور ٹیکنالوجی پر مبنی قانونی حل فراہم کرنے میں سب سے آگے ہے۔",
      seniorAdv: "ہائی کورٹ چیمبرز کے سینئر ایڈووکیٹ",
      advisorGovt: "سرکاری اداروں اور کارپوریٹ بورڈز کے مشیر",
      reportedCases: "رپورٹ شدہ مقدمات",
      recentEvents: "حالیہ تقریبات",
      chatNode: "لائیو چیٹ نوڈ",
      cjpBadge: "سپریم کورٹ آف پاکستان",
      cjpTitle: "معزز چیف جسٹس جناب جسٹس یحییٰ آفریدی",
      cjpDesc: "جسٹس یحییٰ آفریدی پاکستان کے 30ویں اور موجودہ چیف جسٹس ہیں۔ 23 جنوری 1965 کو پیدا ہونے والے، وہ پاکستان کی اعلیٰ ترین عدالت کے پہلے چیف جسٹس ہیں جن کا تعلق سابقہ وفاقی زیر انتظام قبائلی علاقوں (فاٹا) سے ہے۔",
      fccBadge: "وفاقی آئینی عدالت",
      fccTitle: "معزز چیف جسٹس جناب جسٹس امین الدین خان",
      fccDesc: "جسٹس امین الدین خان آئینی بنچوں کے سربراہ اور وفاقی آئینی عدالت کے چیف جسٹس کے طور پر خدمات انجام دے رہے ہیں۔ انہوں نے 12 مئی 2011 سے 22 اکتوبر 2019 تک لاہور ہائی کورٹ کے جج کے طور پر بھی کام کیا۔ انہیں 13 نومبر 2025 کو وفاقی آئینی عدالت کے پہلے چیف جسٹس کے طور پر مقرر کیا گیا۔",
      bhcBadge: "بلوچستان ہائی کورٹ",
      bhcTitle: "معزز چیف جسٹس جناب جسٹس محمد کامران خان ملاخیل",
      bhcDesc: "جسٹس محمد کامران خان ملاخیل بلوچستان ہائی کورٹ کے موجودہ چیف جسٹس ہیں، جنہوں نے دسمبر 2025 میں عہدہ سنبھالا۔ وہ اگست 2013 سے بلوچستان ہائی کورٹ کے جج کے طور پر خدمات انجام دے رہے ہیں اور آئینی قانون اور عدالتی اصلاحات کا وسیع تجربہ رکھتے ہیں۔",
      desc1: "پاکستان کے ہزاروں نظائر، سپریم کورٹ کے فیصلوں اور قوانین میں فوری تلاش کی خصوصیت۔",
      desc2: "اپنے کیس کی تفصیلات درج کریں اور مصنوعی ذہانت کے ذریعے پیش گوئی حاصل کریں۔",
      desc3: "کیس کے اندراج سے لے کر حتمی فیصلے تک کے مراحل کی نگرانی کریں۔",
      desc4: "ہائی کورٹ کے وکلاء اور ساتھیوں کے لیے ایک ساتھ کام کرنے کی جگہ۔"
    }
  };

  const t = dict[language];

  // Colors based on 3-way theme mode ('dark' | 'light' | 'easy')
  const navyDark = themeMode === 'dark' ? '#070b12' : themeMode === 'easy' ? '#142421' : '#1e293b';
  const navyPrimary = themeMode === 'dark' ? '#0f172a' : themeMode === 'easy' ? '#1b332d' : '#334155';
  const pageBg = themeMode === 'dark' ? '#0a0f1d' : themeMode === 'easy' ? '#f4eedd' : '#f8fafc';
  const cardBg = themeMode === 'dark' ? '#1e293b' : themeMode === 'easy' ? '#fbf7ee' : '#ffffff';
  const textColor = themeMode === 'dark' ? '#e2e8f0' : themeMode === 'easy' ? '#243632' : '#1e293b';
  const titleColor = themeMode === 'dark' ? '#ffffff' : themeMode === 'easy' ? '#0d1f1c' : '#0f172a';
  const textMutedColor = themeMode === 'dark' ? '#94a3b8' : themeMode === 'easy' ? '#526661' : '#475569';
  const goldPrimary = themeMode === 'easy' ? '#b45309' : '#ecc94b';
  const goldMuted = themeMode === 'easy' ? 'rgba(180, 83, 9, 0.35)' : 'rgba(236, 201, 75, 0.4)';
  const borderThemeColor = themeMode === 'dark' ? 'rgba(236,201,75,0.3)' : themeMode === 'easy' ? 'rgba(180, 83, 9, 0.25)' : '#cbd5e1';
  const fontSerif = "'Playfair Display', Georgia, serif";
  const fontSans = language === 'UR' ? "'Noto Nastaliq Urdu', 'Inter', sans-serif" : "'Inter', system-ui, sans-serif";

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <div className="landing-page" dir={language === 'UR' ? 'rtl' : 'ltr'} style={{ minHeight: '100vh', backgroundColor: pageBg, fontFamily: fontSans, color: textColor, transition: 'all 0.3s ease' }}>
      
      {/* REAL-TIME MARQUEE TICKER BANNER */}
      <MarqueeTicker />

      {/* 1. SOPHISTICATED 2-LINE NAVIGATION BAR (Full Width Logo Strip & Dedicated Menu Strip) */}
      <header style={{ 
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: navyDark, 
        color: 'white', 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        boxShadow: '0 8px 30px rgba(0,0,0,0.45)',
        borderBottom: `3px solid ${goldPrimary}`,
        overflow: 'visible'
      }}>
        {/* LINE 1: EXTREME LEFT BRANDING & EXTREME RIGHT UTILITIES (1.5x Height Expanded Strip) */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '1.15rem 2.5rem',
          borderBottom: '1px solid rgba(236,201,75,0.3)',
          width: '100%',
          boxSizing: 'border-box',
          backgroundColor: themeMode === 'dark' ? '#04070d' : themeMode === 'easy' ? '#0f1c19' : '#0f172a',
          minHeight: '100px'
        }}>
          {/* Logo & Firm Title on EXTREME LEFT - Square Shape Logo & Shaded Font */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
            <img 
              src="/logo_ELum_Firm4.png" 
              alt="Elum Law Firm Logo" 
              style={{ 
                height: '78px', 
                width: '78px',
                objectFit: 'cover',
                borderRadius: '12px',
                border: `3px solid ${goldPrimary}`,
                boxShadow: '0 0 20px rgba(236,201,75,0.5)',
                backgroundColor: '#04060b',
                flexShrink: 0
              }} 
            />
            <div style={{ textAlign: language === 'UR' ? 'right' : 'left' }}>
              <h1 style={{ 
                fontSize: '1.85rem', 
                fontWeight: '900', 
                fontFamily: fontSerif, 
                letterSpacing: language === 'UR' ? '0' : '0.04em', 
                margin: 0, 
                color: goldPrimary, 
                lineHeight: '1.15',
                textShadow: '2px 2px 8px rgba(0, 0, 0, 0.95), 0 0 15px rgba(236, 201, 75, 0.6), 0 0 30px rgba(236, 201, 75, 0.3)',
                WebkitTextStroke: '0.4px #ffffff'
              }}>
                {language === 'EN' ? 'ELUM LAW ASSOCIATES' : 'ایلم لا ایسوسی ایٹس'}
              </h1>
              <span style={{ 
                fontSize: '0.88rem', 
                color: '#ffffff', 
                letterSpacing: language === 'UR' ? '0' : '0.14em', 
                textTransform: 'uppercase', 
                display: 'block', 
                fontWeight: '800', 
                marginTop: '4px',
                textShadow: '1px 1px 4px rgba(0,0,0,0.9)'
              }}>
                {language === 'EN' ? 'AI Powered Legal Node & Judicial Repository • Pakistan' : 'اے آئی سے لیس قانونی مرکز • پاکستان'}
              </span>
            </div>
          </div>

          {/* Extreme Right Utilities: Language Switcher, 3-Way Theme Control, User Profile */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            flexShrink: 0
          }}>
            {/* UR/EN LANGUAGE SWITCHER */}
            <button 
              onClick={() => setLanguage(language === 'EN' ? 'UR' : 'EN')}
              style={{
                backgroundColor: 'transparent',
                border: `1.5px solid ${goldPrimary}`,
                borderRadius: '6px',
                color: goldPrimary,
                padding: '0.45rem 1rem',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 0 10px rgba(236,201,75,0.2)'
              }}
            >
              🌐 {language === 'EN' ? 'اردو' : 'English'}
            </button>

            {/* 3-WAY THEME MODE SWITCHER (Dark, Light, Easy-on-the-Eyes) */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: 'rgba(255,255,255,0.08)', 
              padding: '0.25rem', 
              borderRadius: '8px', 
              border: '1px solid rgba(255,255,255,0.18)' 
            }}>
              <button 
                onClick={() => setThemeMode('dark')}
                style={{
                  backgroundColor: themeMode === 'dark' ? goldPrimary : 'transparent',
                  color: themeMode === 'dark' ? '#0f172a' : '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="Midnight Dark Mode"
              >
                🌙 Dark
              </button>
              <button 
                onClick={() => setThemeMode('light')}
                style={{
                  backgroundColor: themeMode === 'light' ? goldPrimary : 'transparent',
                  color: themeMode === 'light' ? '#0f172a' : '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="Parchment Light Mode"
              >
                ☀️ Light
              </button>
              <button 
                onClick={() => setThemeMode('easy')}
                style={{
                  backgroundColor: themeMode === 'easy' ? '#10b981' : 'transparent',
                  color: themeMode === 'easy' ? '#ffffff' : '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="Easy on the Eyes (Warm Comfort View)"
              >
                👁️ Eye-Comfort
              </button>
            </div>

            {/* USER PROFILE / LOGOUT / LOGIN */}
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Link to="/dashboard" style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  fontSize: '0.95rem',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  fontWeight: 'bold'
                }}>
                  <LayoutDashboard size={18} color={goldPrimary} /> {t.dashboard}
                </Link>
                <button 
                  onClick={handleLogout}
                  style={{ 
                    background: 'none', 
                    border: '1px solid #ef4444', 
                    backgroundColor: 'rgba(239,68,68,0.18)',
                    color: '#fca5a5', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.4rem', 
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    padding: '0.5rem 0.9rem',
                    borderRadius: '8px'
                  }}
                  title="Logout from account"
                >
                  <LogOut size={16} /> {t.logout}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Link to="/login" style={{ color: 'white', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 600 }}>{t.login}</Link>
                <Link to="/register" style={{ 
                  backgroundColor: goldPrimary, 
                  color: navyDark, 
                  padding: '0.55rem 1.35rem', 
                  borderRadius: '8px', 
                  textDecoration: 'none', 
                  fontWeight: '800', 
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 15px rgba(236,201,75,0.4)'
                }}>
                  {t.getStarted}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* LINE 2: SECONDARY SINGLE STRIP FOR ALL NAVIGATION MENUS & BUTTONS (1.5x Enhanced Padding & Typography) */}
        <div style={{ 
          padding: '0.75rem 1.75rem', 
          backgroundColor: themeMode === 'dark' ? '#0d1527' : themeMode === 'easy' ? '#172b27' : '#1e293b',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          position: 'relative',
          overflow: 'visible',
          zIndex: 1001,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <nav style={{ 
            display: 'flex', 
            gap: '1rem', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexWrap: 'wrap', 
            position: 'relative',
            overflow: 'visible',
            maxWidth: '100%'
          }}>
            {/* BADGE 1: ELUM LIVE CHAT (FLOATING WINDOW WITHIN WINDOW) */}
            {/* BADGE 1: ELUM LIVE CHAT */}
            <button 
              onClick={() => {
                if (user) {
                  if (showLiveChatWindow) {
                    window.dispatchEvent(new CustomEvent('openLiveChat'));
                  } else {
                    setShowLiveChatWindow(true);
                  }
                } else {
                  setShowAccessModal(true);
                }
              }}
              style={{ 
                backgroundColor: '#16a34a', 
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.35rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 14px rgba(22, 163, 74, 0.75)',
                whiteSpace: 'nowrap'
              }}
            >
              <MessageSquare size={18} />
              <span>Elum Live Chat</span>
            </button>

            {/* BADGE 2: ELUM LAW DIRECTORY */}
            <button 
              onClick={() => {
                if (user) {
                  if (showLawDirectoryModal) {
                    window.dispatchEvent(new CustomEvent('openLawDirectory'));
                  } else {
                    setShowLawDirectoryModal(true);
                  }
                } else {
                  setShowAccessModal(true);
                }
              }}
              style={{ 
                backgroundColor: '#059669', 
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.35rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 14px rgba(5, 150, 105, 0.75)',
                whiteSpace: 'nowrap'
              }}
            >
              <Scale size={18} />
              <span>Elum Law Directory</span>
            </button>

            {/* BADGE 3: LAW DICTIONARY */}
            <button 
              onClick={() => {
                if (user) {
                  if (showDictionaryModal) {
                    window.dispatchEvent(new CustomEvent('openLawDictionary'));
                  } else {
                    setShowDictionaryModal(true);
                  }
                } else {
                  setShowAccessModal(true);
                }
              }}
              style={{ 
                backgroundColor: '#15803d', 
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.35rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 14px rgba(21, 128, 61, 0.85)',
                whiteSpace: 'nowrap'
              }}
            >
              <BookOpen size={18} color="#ecc94b" />
              <span>Law Dictionary</span>
            </button>

            {/* BADGE 4: SMART LEGAL DRAFTING & PLEADING */}
            <button 
              onClick={() => {
                if (user) {
                  if (showSmartDraftingModal) {
                    window.dispatchEvent(new CustomEvent('openSmartDrafting'));
                  } else {
                    setShowSmartDraftingModal(true);
                  }
                } else {
                  setShowAccessModal(true);
                }
              }}
              style={{ 
                backgroundColor: '#d97706', 
                color: '#0f172a',
                border: 'none',
                padding: '0.65rem 1.45rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '900',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 16px rgba(217, 119, 6, 0.9)',
                whiteSpace: 'nowrap'
              }}
            >
              <Gavel size={18} />
              <span>Smart Legal Drafting & Pleading</span>
            </button>

            {/* BADGE 5: AI LEGAL ASSISTANT */}
            <button 
              onClick={() => {
                if (user) {
                  if (showAiAssistantModal) {
                    window.dispatchEvent(new CustomEvent('openAiAssistant'));
                  } else {
                    setShowAiAssistantModal(true);
                  }
                } else {
                  setShowAccessModal(true);
                }
              }}
              style={{ 
                backgroundColor: '#4f46e5', 
                color: 'white',
                border: 'none',
                padding: '0.65rem 1.35rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 14px rgba(79, 70, 229, 0.85)',
                whiteSpace: 'nowrap'
              }}
            >
              <Shield size={18} />
              <span>AI Legal Assistant</span>
            </button>

            {/* BADGE 6: ELUM MEET (ZOOM VIDEO CONFERENCE & PiP) */}
            <button 
              onClick={() => {
                setShowVideoConferenceModal(true);
              }}
              style={{ 
                backgroundColor: '#0284c7', 
                color: 'white',
                border: '2px solid #38bdf8',
                padding: '0.65rem 1.45rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '900',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 18px rgba(2, 132, 199, 0.95)',
                whiteSpace: 'nowrap',
                animation: 'pulse 2s infinite'
              }}
            >
              <Video size={19} color="#ecc94b" />
              <span>📹 Elum Meet</span>
            </button>

            {/* BADGE 7: DOWNLOADS & DIGITAL PUBLICATIONS */}
            <Link
              to="/downloads"
              style={{
                backgroundColor: '#7e22ce',
                color: 'white',
                textDecoration: 'none',
                border: '2px solid #c084fc',
                padding: '0.65rem 1.45rem',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: '900',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 0 18px rgba(126, 34, 206, 0.9)',
                whiteSpace: 'nowrap'
              }}
            >
              <Download size={19} color="#ecc94b" />
              <span>Downloads</span>
              <span style={{
                backgroundColor: '#ecc94b',
                color: '#0f172a',
                fontSize: '0.72rem',
                fontWeight: '900',
                padding: '0.15rem 0.45rem',
                borderRadius: '12px',
                marginLeft: '0.2rem'
              }}>
                NEW
              </span>
            </Link>

          </nav>
        </div>
      </header>

      {/* 2. REPORTED CASES & NEW EVENTS PICTURES MARQUEE (BELOW HEADER) */}
      <section style={{ 
        background: 'linear-gradient(to right, #090d16, #1e293b)', 
        borderBottom: `3px solid ${goldPrimary}`,
        overflow: 'hidden'
      }}>
        {/* Marquee 1: Reported Cases (Full Width - 26ms staggered delay) */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '1rem 2.5rem', borderBottom: '1px solid rgba(236,201,75,0.2)' }}>
          {React.createElement('marquee', { scrollamount: '3.5', scrolldelay: '26', style: { color: goldPrimary, fontSize: '0.95rem', fontWeight: 'bold', width: '100%' } }, 
            (marquee1Items.length > 0 ? marquee1Items : [
              { _id: 'c1', title: '⚖️ PLD 2026 SC 101 - Constitutional Amendment Ref: Benches Division', linkUrl: '/cases/pld-2026-sc-101.html' },
              { _id: 'c2', title: '⚖️ SCMR 2025 845 - Appellate Jurisdiction & Civil Procedure Enforcements', linkUrl: '/cases/scmr-2025-845.html' },
              { _id: 'c3', title: '⚖️ PLD 2025 BHC 72 - Provincial Judicial Digitization and Court Records Verification', linkUrl: '/cases/pld-2025-bhc-72.html' },
              { _id: 'c4', title: '⚖️ CLC 2026 Karachi 190 - Corporate Mergers & Regulatory Frameworks', linkUrl: '/cases/clc-2026-karachi-190.html' },
              { _id: 'c5', title: '⚖️ PLD 2026 Supreme Court 55 - AI-Assisted Automated Judgment Guidelines', linkUrl: '/cases/pld-2026-sc-55.html' }
            ]).map((item: any, idx: number) => (
              <span 
                key={item._id || idx} 
                onClick={() => handleOpenCaseDetail(item)}
                style={{ color: goldPrimary, textDecoration: 'none', marginRight: '4rem', display: 'inline-block', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.color = '#38bdf8')}
                onMouseOut={(e) => (e.currentTarget.style.color = goldPrimary)}
              >
                {item.title}
              </span>
            ))
          )}
        </div>
        
        {/* Marquee 2: New Events Pictures & Video Clips (Full Width - 36ms staggered delay) */}
        <div style={{ padding: '1.75rem 2.5rem', display: 'flex', alignItems: 'center', minHeight: '460px' }}>
          {React.createElement('marquee', { scrollamount: '3', scrolldelay: '36', style: { height: '430px', width: '100%', whiteSpace: 'nowrap' } }, 
            (marquee2Items.length > 0 ? marquee2Items : [
              { _id: 'e1', title: 'CJP Oath Ceremony', subtitle: 'Tap to view description and video transmission', imageUrl: '/Justice_Yahya_Afridi_CJP.jpeg', linkUrl: '/events/cjp-oath.html' },
              { _id: 'e2', title: 'FCC Head Inauguration', subtitle: 'Tap to view description and video transmission', imageUrl: '/Justice-Amin-ud-Din-Khan-CJ-FCC.jpeg', linkUrl: '/events/fcc-inauguration.html' },
              { _id: 'e3', title: 'BHC e-Filing Rollout', subtitle: 'Tap to view description and video transmission', imageUrl: '/justice-muhammad-kamran-mulakhail-CJ_BHC.jpg', linkUrl: '/events/bhc-automation.html' },
              { _id: 'e4', title: 'Elum AI Launch Meet', subtitle: 'Tap to view description and video transmission', imageUrl: '/owner.jpeg', linkUrl: '/events/elum-launch.html' }
            ]).map((item: any, idx: number) => (
              <div 
                key={item._id || idx} 
                onClick={() => handleOpenEventDetail(item)}
                style={{ 
                  display: 'inline-flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '0.75rem', 
                  color: 'white', 
                  marginRight: 'calc(4rem + 0.75in)', 
                  height: '410px', 
                  width: 'calc(360px + 0.75in)',
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(15, 23, 42, 0.65)',
                  padding: '1rem',
                  borderRadius: '18px',
                  border: `2px solid rgba(236,201,75,0.5)`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, border-color 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#ecc94b';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(236,201,75,0.5)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {item.imageUrl && (
                  <img 
                    src={item.imageUrl} 
                    alt={item.title} 
                    style={{ 
                      height: '280px', 
                      width: '100%', 
                      borderRadius: '12px', 
                      border: `3px solid ${goldPrimary}`, 
                      objectFit: 'cover', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.6)' 
                    }} 
                  />
                )}
                {/* DESCRIPTIVE TEXT SHOWN JUST BELOW IMAGE / VIDEO CLIP */}
                <div style={{ whiteSpace: 'normal', textAlign: 'center', width: '100%' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: '800', color: goldPrimary, display: 'block', marginBottom: '0.25rem', lineHeight: '1.3' }}>{item.title}</span>
                  <span style={{ fontSize: '0.92rem', color: '#cbd5e1', display: 'block', lineHeight: '1.4' }}>{item.subtitle || 'Tap to view details & live broadcast clip'}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <main>
        
        {/* 3. IMPERIAL PRESTIGE HERO SECTION */}
        <section style={{ 
          textAlign: 'center', 
          padding: '3.5rem 2rem 3rem', 
          background: `linear-gradient(135deg, ${navyDark} 0%, ${navyPrimary} 100%)`, 
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            opacity: 0.05,
            backgroundImage: 'radial-gradient(#ecc94b 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} />
          
          <div style={{ maxWidth: '1350px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            
            {/* WELCOME BANNER & LOGIN / REGISTRATION PANEL FOR https://www.elumlaw.com */}
            <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: `2px solid ${goldPrimary}`, borderRadius: '16px', padding: '1.5rem 2rem', marginBottom: '2rem', boxShadow: '0 12px 35px rgba(236,201,75,0.25)' }}>
              <h1 style={{ fontSize: '1.85rem', fontWeight: '900', color: goldPrimary, margin: 0, letterSpacing: '0.02em', lineHeight: '1.3' }}>
                🏛️ Welcome to Pakistan's AI Powered Elum Legal Services & Law Directory
              </h1>
              <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.98rem', color: '#cbd5e1', fontWeight: '600' }}>
                Digital Legal Portal for Advocates, Practitioners, Honourable Judges, Govt Entities & Valued Clients
              </p>

              {!user && (
                <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <Link to="/login" style={{ backgroundColor: goldPrimary, color: navyDark, padding: '0.75rem 2rem', borderRadius: '10px', textDecoration: 'none', fontWeight: '900', fontSize: '0.95rem', boxShadow: '0 4px 15px rgba(236,201,75,0.4)', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    🔑 Sign In / Login Panel
                  </Link>
                  <Link to="/register" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', padding: '0.75rem 2rem', borderRadius: '10px', textDecoration: 'none', fontWeight: '800', fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    📝 Register New Account
                  </Link>
                </div>
              )}
            </div>

            {/* -------------------------------------------------------------------------------- */}
            {/* LATEST CASES & LATEST STATUTES 2-COLUMN TABULAR REAL-TIME STREAM */}
            {/* -------------------------------------------------------------------------------- */}
            <style>{`
              .blink-badge-red {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                background-color: #dc2626;
                color: #ffffff;
                font-size: 0.72rem;
                font-weight: 900;
                padding: 0.25rem 0.65rem;
                border-radius: 20px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                border: 1px solid #f87171;
                white-space: nowrap;
                flex-shrink: 0;
                box-shadow: 0 2px 6px rgba(220, 38, 38, 0.4);
              }
              .blink-badge-gold {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                background-color: #d97706;
                color: #ffffff;
                font-size: 0.72rem;
                font-weight: 900;
                padding: 0.25rem 0.65rem;
                border-radius: 20px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                border: 1px solid #fbbf24;
                white-space: nowrap;
                flex-shrink: 0;
                box-shadow: 0 2px 6px rgba(217, 119, 6, 0.4);
              }
              .legal-stream-table tr:hover td {
                background-color: rgba(236, 201, 75, 0.08) !important;
              }
            `}</style>

            <div style={{
              backgroundColor: 'rgba(15, 23, 42, 0.96)',
              border: '1px solid rgba(236, 201, 75, 0.35)',
              borderRadius: '16px',
              padding: '1.75rem 1.5rem',
              marginBottom: '3rem',
              boxShadow: '0 15px 40px rgba(0, 0, 0, 0.45)',
              textAlign: 'left'
            }}>
              {/* STREAM TITLE HEADER */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.35rem', fontWeight: '900', color: '#ffffff', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
                      ⚡ Real-Time Legal Stream
                    </span>
                    <span className="blink-badge-red">
                      🔴 LIVE REPOSITORY FEED
                    </span>
                  </div>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                    Direct access to recently adjudicated landmark precedents and Gazette statutory enactments
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                  <Link
                    to="/law-directory"
                    style={{
                      backgroundColor: 'rgba(30, 58, 138, 0.6)',
                      color: '#93c5fd',
                      border: '1px solid #3b82f6',
                      padding: '0.45rem 1rem',
                      borderRadius: '8px',
                      fontSize: '0.82rem',
                      fontWeight: '800',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    ⚖️ Case Law Directory (150,036) <ArrowUpRight size={14} />
                  </Link>

                  <Link
                    to="/statutes"
                    style={{
                      backgroundColor: 'rgba(180, 83, 9, 0.25)',
                      color: '#fde68a',
                      border: '1px solid #f59e0b',
                      padding: '0.45rem 1rem',
                      borderRadius: '8px',
                      fontSize: '0.82rem',
                      fontWeight: '800',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    📜 Statutes Directory (10,768) <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>

              {/* 2-COLUMN TABULAR GRID */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(580px, 1fr))',
                gap: '1.5rem',
                alignItems: 'start'
              }}>

                {/* ========================================================================= */}
                {/* COLUMN 1: LATEST CASES & PRECEDENTS */}
                {/* ========================================================================= */}
                <div style={{
                  backgroundColor: '#090d16',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Table Header Banner */}
                  <div style={{
                    backgroundColor: 'rgba(30, 58, 138, 0.4)',
                    padding: '0.9rem 1.25rem',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Scale size={20} color="#60a5fa" />
                      <span style={{ fontSize: '1rem', fontWeight: '800', color: '#ffffff', whiteSpace: 'nowrap' }}>
                        Latest Judicial Precedents
                      </span>
                    </div>
                    <span className="blink-badge-red">
                      ⚡ NEW
                    </span>
                  </div>

                  {/* Tabular Table */}
                  <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
                    <table className="legal-stream-table" style={{ minWidth: '600px', width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', color: '#e2e8f0', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <th style={{ padding: '0.75rem 0.85rem' }}>Case Title & Parties</th>
                          <th style={{ padding: '0.75rem 0.6rem', whiteSpace: 'nowrap', width: '135px' }}>Court Name</th>
                          <th style={{ padding: '0.75rem 0.6rem', whiteSpace: 'nowrap', width: '125px' }}>Citation Source</th>
                          <th style={{ padding: '0.75rem 0.6rem', textAlign: 'center', whiteSpace: 'nowrap', width: '90px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latestCases.slice(0, 8).map((cItem, idx) => {
                          const courtStyle = (() => {
                            const c = (cItem.court || cItem.court_name || '').toLowerCase();
                            if (c.includes('supreme court')) return { bg: '#1e3a8a', border: '#3b82f6', text: '#bfdbfe', label: 'Supreme Court' };
                            if (c.includes('sindh')) return { bg: '#064e3b', border: '#10b981', text: '#a7f3d0', label: 'Sindh High Court' };
                            if (c.includes('lahore')) return { bg: '#312e81', border: '#6366f1', text: '#c7d2fe', label: 'Lahore High Court' };
                            if (c.includes('islamabad')) return { bg: '#581c87', border: '#a855f7', text: '#e9d5ff', label: 'Islamabad High Court' };
                            if (c.includes('balochistan')) return { bg: '#78350f', border: '#f59e0b', text: '#fde68a', label: 'Balochistan HC' };
                            if (c.includes('peshawar') || c.includes('kpk')) return { bg: '#0f766e', border: '#14b8a6', text: '#99f6e4', label: 'Peshawar HC' };
                            return { bg: '#1e293b', border: '#64748b', text: '#f1f5f9', label: cItem.court || 'High Court' };
                          })();

                          const citationText = cItem.citationNumber || cItem.citation || cItem.case_id || `${cItem.year || '2026'} SCMR ${idx + 101}`;

                          return (
                            <tr
                              key={cItem.id || idx}
                              style={{
                                borderBottom: '1px solid rgba(51, 65, 85, 0.6)',
                                backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)',
                                transition: 'background-color 0.15s ease'
                              }}
                            >
                              {/* Case Title (Hyperlinked with clean horizontal NEW badge) */}
                              <td style={{ padding: '0.75rem 0.85rem', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {idx < 2 && (
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.64rem',
                                      backgroundColor: '#dc2626',
                                      color: '#ffffff',
                                      fontWeight: '900',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                      letterSpacing: '0.04em',
                                      lineHeight: '1'
                                    }}>
                                      NEW
                                    </span>
                                  )}
                                  <button
                                    onClick={() => setSelectedLandingDoc(cItem)}
                                    title="Click to view full precedent summary & judicial analysis"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      margin: 0,
                                      color: '#60a5fa',
                                      fontWeight: '700',
                                      fontSize: '0.84rem',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      textDecoration: 'none',
                                      lineHeight: '1.4'
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                  >
                                    {cItem.title || cItem.name || 'Landmark Judicial Precedent'}
                                  </button>
                                </div>
                              </td>

                              {/* Court Name (Highlighted Pill) */}
                              <td style={{ padding: '0.75rem 0.6rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  backgroundColor: courtStyle.bg,
                                  border: `1px solid ${courtStyle.border}`,
                                  color: courtStyle.text,
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '6px',
                                  fontSize: '0.74rem',
                                  fontWeight: '800',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  boxShadow: `0 0 6px ${courtStyle.bg}`
                                }}>
                                  🏛️ {courtStyle.label}
                                </span>
                              </td>

                              {/* Citation Source (Highlighted Pill) */}
                              <td style={{ padding: '0.75rem 0.6rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  backgroundColor: '#451a03',
                                  border: '1px solid #f59e0b',
                                  color: '#fef08a',
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '800',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  boxShadow: '0 0 8px rgba(245, 158, 11, 0.25)'
                                }}>
                                  📜 {citationText}
                                </span>
                              </td>

                              {/* Action Button (Always cleanly horizontal) */}
                              <td style={{ padding: '0.75rem 0.6rem', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <button
                                  onClick={() => setSelectedLandingDoc(cItem)}
                                  title="View Case Analysis"
                                  style={{
                                    backgroundColor: 'rgba(59, 130, 246, 0.18)',
                                    color: '#93c5fd',
                                    border: '1px solid #3b82f6',
                                    padding: '0.35rem 0.7rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.3rem',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    transition: 'all 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                >
                                  <span>View</span>
                                  <span style={{ fontSize: '0.85rem' }}>➔</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Table Footer */}
                  <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', padding: '0.75rem 1.25rem', borderTop: '1px solid #334155', textAlign: 'center' }}>
                    <Link
                      to="/law-directory"
                      style={{
                        color: '#60a5fa',
                        fontSize: '0.82rem',
                        fontWeight: '800',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Browse All 150,000+ Precedents in Law Directory ➔
                    </Link>
                  </div>
                </div>

                {/* ========================================================================= */}
                {/* COLUMN 2: LATEST STATUTES & ACTS */}
                {/* ========================================================================= */}
                <div style={{
                  backgroundColor: '#090d16',
                  borderRadius: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Table Header Banner */}
                  <div style={{
                    backgroundColor: 'rgba(180, 83, 9, 0.3)',
                    padding: '0.9rem 1.25rem',
                    borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Landmark size={20} color="#fbbf24" />
                      <span style={{ fontSize: '1rem', fontWeight: '800', color: '#ffffff', whiteSpace: 'nowrap' }}>
                        Latest Statutory Enactments & Acts
                      </span>
                    </div>
                    <span className="blink-badge-gold">
                      ✨ NEW
                    </span>
                  </div>

                  {/* Tabular Table */}
                  <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
                    <table className="legal-stream-table" style={{ minWidth: '600px', width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', color: '#e2e8f0', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <th style={{ padding: '0.75rem 0.85rem' }}>Statute / Act Name</th>
                          <th style={{ padding: '0.75rem 0.6rem', whiteSpace: 'nowrap', width: '145px' }}>Jurisdiction</th>
                          <th style={{ padding: '0.75rem 0.6rem', whiteSpace: 'nowrap', width: '135px' }}>Citation / Enactment</th>
                          <th style={{ padding: '0.75rem 0.6rem', textAlign: 'center', whiteSpace: 'nowrap', width: '90px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latestStatutes.slice(0, 8).map((sItem, idx) => {
                          const jurisStyle = (() => {
                            const j = (sItem.jurisdiction || '').toLowerCase();
                            if (j.includes('federal')) return { bg: '#1e3a8a', border: '#3b82f6', text: '#bfdbfe', icon: '🏛️', label: 'Federal / Parliament' };
                            if (j.includes('punjab')) return { bg: '#312e81', border: '#6366f1', text: '#c7d2fe', icon: '🌾', label: 'Punjab Assembly' };
                            if (j.includes('sindh')) return { bg: '#064e3b', border: '#10b981', text: '#a7f3d0', icon: '🌊', label: 'Sindh Assembly' };
                            if (j.includes('kpk') || j.includes('khyber')) return { bg: '#0f766e', border: '#14b8a6', text: '#99f6e4', icon: '🌲', label: 'KPK Assembly' };
                            if (j.includes('balochistan')) return { bg: '#78350f', border: '#f59e0b', text: '#fde68a', icon: '⛰️', label: 'Balochistan Assembly' };
                            if (j.includes('ajk') || j.includes('kashmir')) return { bg: '#581c87', border: '#a855f7', text: '#e9d5ff', icon: '🏔️', label: 'AJK Assembly' };
                            return { bg: '#1e293b', border: '#64748b', text: '#f1f5f9', icon: '⚖️', label: sItem.jurisdiction || 'Federal' };
                          })();

                          const citationType = sItem.legislation_type || sItem.notification_number || sItem.sro_number || `Act ${idx + 1} of ${sItem.year || '2026'}`;

                          return (
                            <tr
                              key={sItem.id || idx}
                              style={{
                                borderBottom: '1px solid rgba(51, 65, 85, 0.6)',
                                backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)',
                                transition: 'background-color 0.15s ease'
                              }}
                            >
                              {/* Statute Title (Hyperlinked with clean horizontal NEW badge) */}
                              <td style={{ padding: '0.75rem 0.85rem', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {idx < 2 && (
                                    <span style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.64rem',
                                      backgroundColor: '#d97706',
                                      color: '#ffffff',
                                      fontWeight: '900',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                      letterSpacing: '0.04em',
                                      lineHeight: '1'
                                    }}>
                                      NEW
                                    </span>
                                  )}
                                  <button
                                    onClick={() => setSelectedLandingDoc(sItem)}
                                    title="Click to view full statute & section-by-section navigator"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                      margin: 0,
                                      color: '#fbbf24',
                                      fontWeight: '700',
                                      fontSize: '0.84rem',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      textDecoration: 'none',
                                      lineHeight: '1.4'
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                  >
                                    {sItem.title || sItem.name || 'Statutory Enactment'}
                                  </button>
                                </div>
                              </td>

                              {/* Jurisdiction (Highlighted Pill) */}
                              <td style={{ padding: '0.75rem 0.6rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  backgroundColor: jurisStyle.bg,
                                  border: `1px solid ${jurisStyle.border}`,
                                  color: jurisStyle.text,
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '6px',
                                  fontSize: '0.74rem',
                                  fontWeight: '800',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  boxShadow: `0 0 6px ${jurisStyle.bg}`
                                }}>
                                  {jurisStyle.icon} {jurisStyle.label}
                                </span>
                              </td>

                              {/* Citation Source / Enactment No. (Highlighted Pill) */}
                              <td style={{ padding: '0.75rem 0.6rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  backgroundColor: '#064e3b',
                                  border: '1px solid #10b981',
                                  color: '#a7f3d0',
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '800',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  boxShadow: '0 0 8px rgba(16, 185, 129, 0.25)'
                                }}>
                                  📜 {citationType}
                                </span>
                              </td>

                              {/* Action Button (Always cleanly horizontal) */}
                              <td style={{ padding: '0.75rem 0.6rem', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <button
                                  onClick={() => setSelectedLandingDoc(sItem)}
                                  title="View Statute Sections"
                                  style={{
                                    backgroundColor: 'rgba(245, 158, 11, 0.18)',
                                    color: '#fde68a',
                                    border: '1px solid #f59e0b',
                                    padding: '0.35rem 0.7rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.3rem',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    transition: 'all 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                >
                                  <span>Read</span>
                                  <span style={{ fontSize: '0.85rem' }}>➔</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Table Footer */}
                  <div style={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', padding: '0.75rem 1.25rem', borderTop: '1px solid #334155', textAlign: 'center' }}>
                    <Link
                      to="/statutes"
                      style={{
                        color: '#fbbf24',
                        fontSize: '0.82rem',
                        fontWeight: '800',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Browse All 10,768+ Acts in Statutes Directory ➔
                    </Link>
                  </div>
                </div>

              </div>
            </div>

            <span style={{ 
              fontSize: '0.8rem', 
              color: goldPrimary, 
              letterSpacing: '0.2em', 
              textTransform: 'uppercase', 
              fontWeight: 'bold',
              border: `1px solid ${goldMuted}`,
              padding: '0.4rem 1.2rem',
              borderRadius: '20px',
              backgroundColor: 'rgba(236, 201, 75, 0.05)'
            }}>
              {t.subBadge}
            </span>
            
            <h2 style={{ 
              fontSize: '3.5rem', 
              fontWeight: 700, 
              fontFamily: fontSerif, 
              marginTop: '2rem', 
              marginBottom: '1.5rem',
              lineHeight: '1.25',
              backgroundImage: 'linear-gradient(to right, #ffffff, #f1f5f9, #ecc94b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t.heroTitle}
            </h2>
            
            <p style={{ 
              fontSize: '1.2rem', 
              opacity: 0.85, 
              maxWidth: '750px', 
              margin: '0 auto 3rem', 
              lineHeight: '1.65',
              fontWeight: 300
            }}>
              {t.heroDesc}
            </p>
            
            {!user && (
              <Link 
                to="/register" 
                onMouseEnter={() => setHoveredBtn(true)}
                onMouseLeave={() => setHoveredBtn(false)}
                style={{ 
                  backgroundColor: goldPrimary, 
                  color: navyDark, 
                  padding: '1.1rem 2.75rem', 
                  borderRadius: '8px', 
                  textDecoration: 'none', 
                  fontWeight: 'bold', 
                  fontSize: '1.05rem',
                  boxShadow: hoveredBtn ? '0 10px 25px rgba(236, 201, 75, 0.35)' : '0 6px 15px rgba(236, 201, 75, 0.2)',
                  transition: 'all 0.3s ease',
                  transform: hoveredBtn ? 'translateY(-2px)' : 'translateY(0)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  letterSpacing: '0.02em'
                }}
              >
                {t.heroBtn} <ChevronRight size={20} />
              </Link>
            )}
          </div>
        </section>

        {/* 4. HONORABLE JUDICIARY CARDS (SUPREME CONSTITUTIONAL SUPREMACY - FULL WIDTH SEPARATELY) */}
        <section id="judiciary" style={{ padding: '6rem 2rem 7rem', backgroundColor: cardBg, borderBottom: `1px solid ${borderThemeColor}`, transition: 'background-color 0.3s' }}>
          <div style={{ maxWidth: '1350px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
              <span style={{ fontSize: '0.85rem', color: textMutedColor, fontWeight: 'bold', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.judiciarySub}</span>
              <h2 style={{ fontSize: '2.6rem', fontWeight: 'bold', color: titleColor, fontFamily: fontSerif, marginTop: '0.5rem', marginBottom: '1rem' }}>
                {t.judiciaryTitle}
              </h2>
              <div style={{ width: '70px', height: '3px', backgroundColor: goldPrimary, margin: '0 auto' }} />
            </div>
            
            <div className="judiciary-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2.5rem',
              alignItems: 'stretch'
            }}>
              {/* Card 1: Supreme Court */}
              <div 
                onMouseEnter={() => setHoveredCard(1)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ 
                  backgroundColor: pageBg, borderRadius: '16px', padding: '2.5rem 2rem', 
                  boxShadow: hoveredCard === 1 ? '0 20px 40px rgba(15,23,42,0.12)' : '0 10px 20px rgba(15,23,42,0.04)', 
                  border: `1px solid ${hoveredCard === 1 ? goldPrimary : borderThemeColor}`, 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', 
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 1 ? 'translateY(-5px)' : 'translateY(0)'
                }}
              >
                <div style={{ 
                  width: '180px', height: '180px', borderRadius: '50%', overflow: 'hidden', 
                  border: `4px solid ${goldPrimary}`, marginBottom: '1.75rem', 
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  flexShrink: 0
                }}>
                  <img src="/Justice_Yahya_Afridi_CJP.jpeg" alt="Chief Justice SCP" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: goldPrimary, textTransform: 'uppercase', letterSpacing: '0.08em', backgroundColor: navyDark, padding: '0.35rem 1rem', borderRadius: '20px', marginBottom: '1rem' }}>
                  {t.cjpBadge}
                </span>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: titleColor, textAlign: 'center', fontFamily: fontSerif, marginBottom: '0.75rem' }}>
                  {t.cjpTitle}
                </h3>
                <p style={{ fontSize: '0.88rem', color: textMutedColor, textAlign: 'center', lineHeight: '1.65', margin: 0 }}>
                  {t.cjpDesc}
                </p>
              </div>

              {/* Card 2: Federal Constitution Court */}
              <div 
                onMouseEnter={() => setHoveredCard(2)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ 
                  backgroundColor: pageBg, borderRadius: '16px', padding: '2.5rem 2rem', 
                  boxShadow: hoveredCard === 2 ? '0 20px 40px rgba(15,23,42,0.12)' : '0 10px 20px rgba(15,23,42,0.04)', 
                  border: `1px solid ${hoveredCard === 2 ? goldPrimary : borderThemeColor}`, 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', 
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 2 ? 'translateY(-5px)' : 'translateY(0)'
                }}
              >
                <div style={{ 
                  width: '180px', height: '180px', borderRadius: '50%', overflow: 'hidden', 
                  border: `4px solid ${goldPrimary}`, marginBottom: '1.75rem', 
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  flexShrink: 0
                }}>
                  <img src="/Justice-Amin-ud-Din-Khan-CJ-FCC.jpeg" alt="Chief Justice FCC" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: goldPrimary, textTransform: 'uppercase', letterSpacing: '0.08em', backgroundColor: navyDark, padding: '0.35rem 1rem', borderRadius: '20px', marginBottom: '1rem' }}>
                  {t.fccBadge}
                </span>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: titleColor, textAlign: 'center', fontFamily: fontSerif, marginBottom: '0.75rem' }}>
                  {t.fccTitle}
                </h3>
                <p style={{ fontSize: '0.88rem', color: textMutedColor, textAlign: 'center', lineHeight: '1.65', margin: 0 }}>
                  {t.fccDesc}
                </p>
              </div>

              {/* Card 3: Balochistan High Court */}
              <div 
                onMouseEnter={() => setHoveredCard(3)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ 
                  backgroundColor: pageBg, borderRadius: '16px', padding: '2.5rem 2rem', 
                  boxShadow: hoveredCard === 3 ? '0 20px 40px rgba(15,23,42,0.12)' : '0 10px 20px rgba(15,23,42,0.04)', 
                  border: `1px solid ${hoveredCard === 3 ? goldPrimary : borderThemeColor}`, 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', 
                  transition: 'all 0.3s ease',
                  transform: hoveredCard === 3 ? 'translateY(-5px)' : 'translateY(0)'
                }}
              >
                <div style={{ 
                  width: '180px', height: '180px', borderRadius: '50%', overflow: 'hidden', 
                  border: `4px solid ${goldPrimary}`, marginBottom: '1.75rem', 
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  flexShrink: 0
                }}>
                  <img src="/justice-muhammad-kamran-mulakhail-CJ_BHC.jpg" alt="Chief Justice BHC" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: goldPrimary, textTransform: 'uppercase', letterSpacing: '0.08em', backgroundColor: navyDark, padding: '0.35rem 1rem', borderRadius: '20px', marginBottom: '1rem' }}>
                  {t.bhcBadge}
                </span>
                <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: titleColor, textAlign: 'center', fontFamily: fontSerif, marginBottom: '0.75rem' }}>
                  {t.bhcTitle}
                </h3>
                <p style={{ fontSize: '0.88rem', color: textMutedColor, textAlign: 'center', lineHeight: '1.65', margin: 0 }}>
                  {t.bhcDesc}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. FOUNDER / OWNER DEDICATED SECTION */}
        <section id="owner" style={{ 
          padding: '6rem 2rem', 
          backgroundColor: navyDark, 
          color: 'white',
          borderTop: `2px solid ${goldMuted}`,
          borderBottom: `2px solid ${goldMuted}`
        }}>
          <div className="owner-grid" style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                border: `3px solid ${goldPrimary}`, 
                borderRadius: '12px', 
                padding: '8px', 
                display: 'inline-block',
                boxShadow: '0 15px 30px rgba(0,0,0,0.4)'
              }}>
                <img 
                  src="/owner.jpeg" 
                  alt="Advocate NajeebUllah" 
                  style={{ width: '100%', maxWidth: '340px', borderRadius: '8px', display: 'block', objectFit: 'cover' }} 
                />
              </div>
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: goldPrimary, letterSpacing: '0.15em', fontWeight: 'bold', textTransform: 'uppercase' }}>{t.founderSub}</span>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', fontFamily: fontSerif, marginTop: '0.5rem', marginBottom: '1.5rem', color: 'white' }}>
                {t.founderTitle}
              </h2>
              <p style={{ fontSize: '1.05rem', color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1.5rem', fontWeight: 300 }}>
                {t.founderDesc}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'rgba(236,201,75,0.1)', color: goldPrimary, width: '40px', height: '40px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Award size={20} /></div>
                  <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{t.seniorAdv}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ backgroundColor: 'rgba(236,201,75,0.1)', color: goldPrimary, width: '40px', height: '40px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><UserCheck size={20} /></div>
                  <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{t.advisorGovt}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. HIGH-VALUE CAPABILITIES SERVICES GRID SECTION (FULL WIDTH SEPARATELY - HORIZONTAL ALIGNMENT) */}
        <section id="services" style={{ padding: '6.5rem 2rem 7.5rem', backgroundColor: pageBg, transition: 'background-color 0.3s' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
              <span style={{ fontSize: '0.85rem', color: textMutedColor, fontWeight: 'bold', letterSpacing: '0.15em', textTransform: 'uppercase' }}>{t.servicesSub}</span>
              <h2 style={{ fontSize: '2.6rem', fontWeight: 'bold', color: titleColor, fontFamily: fontSerif, marginTop: '0.5rem', marginBottom: '1rem' }}>
                {t.servicesTitle}
              </h2>
              <div style={{ width: '70px', height: '3px', backgroundColor: goldPrimary, margin: '0 auto' }} />
            </div>

            <div className="services-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '2rem',
              alignItems: 'stretch'
            }}>
              <FeatureCard 
                index={1}
                activeHover={hoveredFeature}
                onHover={setHoveredFeature}
                themeCardBg={cardBg}
                themeBorder={borderThemeColor}
                themeText={titleColor}
                themeMuted={textMutedColor}
                icon={<BookOpen size={30} />}
                title={language === 'EN' ? "Law Directory & Precedents" : "قوانین اور نظائر کی ڈائریکٹری"}
                description={t.desc1}
                to="/law-directory"
                onClick={(e) => {
                  e.preventDefault();
                  if (user) {
                    if (showLawDirectoryModal) {
                      window.dispatchEvent(new CustomEvent('openLawDirectory'));
                    } else {
                      setShowLawDirectoryModal(true);
                    }
                  } else {
                    setShowAccessModal(true);
                  }
                }}
              />
              <FeatureCard 
                index={2}
                activeHover={hoveredFeature}
                onHover={setHoveredFeature}
                themeCardBg={cardBg}
                themeBorder={borderThemeColor}
                themeText={titleColor}
                themeMuted={textMutedColor}
                icon={<MessageSquare size={30} />}
                title={language === 'EN' ? "AI Advisory Counsel" : "مصنوعی ذہانت کا قانونی مشیر"} 
                description={t.desc2}
                to="/ai-assistant"
                onClick={(e) => {
                  e.preventDefault();
                  if (user) {
                    if (showAiAssistantModal) {
                      window.dispatchEvent(new CustomEvent('openAiAssistant'));
                    } else {
                      setShowAiAssistantModal(true);
                    }
                  } else {
                    setShowAccessModal(true);
                  }
                }}
              />
              <FeatureCard 
                index={3}
                activeHover={hoveredFeature}
                onHover={setHoveredFeature}
                themeCardBg={cardBg}
                themeBorder={borderThemeColor}
                themeText={titleColor}
                themeMuted={textMutedColor}
                icon={<Clock size={30} />}
                title={language === 'EN' ? "Secure Case Timelines" : "محفوظ کیس ٹائم لائنز"} 
                description={t.desc3}
                to="/dashboard?tab=cases"
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault();
                    setShowAccessModal(true);
                  }
                }}
              />
              <FeatureCard 
                index={4}
                activeHover={hoveredFeature}
                onHover={setHoveredFeature}
                themeCardBg={cardBg}
                themeBorder={borderThemeColor}
                themeText={titleColor}
                themeMuted={textMutedColor}
                icon={<Shield size={30} />}
                title={language === 'EN' ? "Elite Counsel Think Tank" : "تھنک ٹینک کونسل"} 
                description={t.desc4}
                to="/dashboard?tab=think-tank-workspace"
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault();
                    setShowAccessModal(true);
                  }
                }}
              />
              <FeatureCard 
                index={5}
                activeHover={hoveredFeature}
                onHover={setHoveredFeature}
                themeCardBg={cardBg}
                themeBorder={borderThemeColor}
                themeText={titleColor}
                themeMuted={textMutedColor}
                icon={<Video size={30} color="#38bdf8" />}
                title={language === 'EN' ? "Elum Meet (Video Conference)" : "ایلم میٹ (ویڈیو کانفرنس)"} 
                description={language === 'EN' ? "Zoom-like judicial video conference platform with Window-in-Window (PiP) multitasking, live screen share, case whiteboard, and Superuser-governed audio/video recording." : "زوم جیسی عدالتی ویڈیو کانفرنس پلیٹ فارم، پکچر ان پکچر ملٹی ٹاسکنگ اور ریکارڈنگ کی سہولت۔"}
                to="/conference"
                onClick={(e) => {
                  e.preventDefault();
                  setShowVideoConferenceModal(true);
                }}
              />
            </div>
          </div>
        </section>

        {/* 6.5 LAW JOURNALS & REPORTS COVERED MATRIX TABLE SECTION */}
        <section id="journals" style={{ padding: '5rem 2rem', backgroundColor: navyDark, color: 'white', borderTop: `2px solid ${goldMuted}`, borderBottom: `2px solid ${goldMuted}` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            
            {/* REAL-TIME LIVE DIRECTORY, DICTIONARY & JUDGMENT MATRIX CARD (APEXED) */}
            <div style={{
              backgroundColor: 'rgba(30, 41, 59, 0.85)',
              backdropFilter: 'blur(12px)',
              borderRadius: '20px',
              border: `2px solid ${goldPrimary}`,
              padding: '2rem 2.5rem',
              marginBottom: '4rem',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: goldPrimary, fontWeight: 'bold', letterSpacing: '0.15em', textTransform: 'uppercase', backgroundColor: 'rgba(236,201,75,0.15)', padding: '0.35rem 1.1rem', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  ⚡ REAL-TIME VERIFIED JUDICIAL MATRIX & DIRECTORY REPOSITORY
                </span>
                <span style={{ backgroundColor: '#10b981', color: 'white', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.65rem', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: 'white', display: 'inline-block', animation: 'pulse 1.5s infinite' }}></span>
                  AUTO-UPDATED {inventoryCounts.lastUpdated}
                </span>
              </div>
              
              <h2 style={{ fontSize: '2.1rem', fontWeight: '800', color: 'white', fontFamily: fontSerif, marginTop: '0.5rem', marginBottom: '1rem' }}>
                Elum Law Live Judicial & Statutory Inventory
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1.5rem',
                margin: '1.5rem 0'
              }}>
                
                {/* METRIC 1: JUDGMENTS */}
                <Link 
                  to="/cases/search" 
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault();
                      setShowAccessModal(true);
                    }
                  }}
                  style={{ textDecoration: 'none', backgroundColor: '#0f172a', padding: '1.5rem 1.25rem', borderRadius: '14px', border: '1px solid #0284c7', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'transform 0.2s' }}
                >
                  <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#38bdf8' }}>
                    {inventoryCounts.cases.toLocaleString()}+
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: '800', color: 'white', marginTop: '0.3rem' }}>🏛️ Case Law Judgments</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem' }}>Full-text (1947–2026)</span>
                </Link>

                {/* METRIC 2: STATUTES & SROS */}
                <Link 
                  to="/statutes/search" 
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault();
                      setShowAccessModal(true);
                    }
                  }}
                  style={{ textDecoration: 'none', backgroundColor: '#0f172a', padding: '1.5rem 1.25rem', borderRadius: '14px', border: '1px solid #eab308', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'transform 0.2s' }}
                >
                  <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#facc15' }}>
                    {inventoryCounts.statutes.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: '800', color: 'white', marginTop: '0.3rem' }}>📜 Statutes, Acts & SROs</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem' }}>Strict Unique Enactments (Federal & 4 Provinces)</span>
                </Link>

                {/* METRIC 3: DICTIONARY */}
                <div 
                  onClick={() => {
                    if (user) {
                      setShowDictionaryModal(true);
                    } else {
                      setShowAccessModal(true);
                    }
                  }}
                  style={{ backgroundColor: '#0f172a', padding: '1.5rem 1.25rem', borderRadius: '14px', border: '1px solid #c084fc', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
                >
                  <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#c084fc' }}>
                    {inventoryCounts.dictionary.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: '800', color: 'white', marginTop: '0.3rem' }}>📖 Law Dictionary Terms</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem' }}>Comprehensive Law & Statutory Terms</span>
                </div>

                {/* METRIC 4: GRAND TOTAL */}
                <div style={{ backgroundColor: '#0f172a', padding: '1.5rem 1.25rem', borderRadius: '14px', border: '2px solid #22c55e', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#4ade80' }}>
                    {inventoryCounts.total.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: '800', color: 'white', marginTop: '0.3rem' }}>⚡ Total Index Inventory</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.2rem' }}>Verified & Searchable (15s Frequency)</span>
                </div>

              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
                <Link 
                  to="/statutes/search" 
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault();
                      setShowAccessModal(true);
                    }
                  }}
                  style={{ backgroundColor: goldPrimary, color: navyDark, fontWeight: '800', padding: '0.75rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  📜 Explore Statutes & SROs Directory
                </Link>
                <Link 
                  to="/cases/search" 
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault();
                      setShowAccessModal(true);
                    }
                  }}
                  style={{ backgroundColor: '#0284c7', color: 'white', fontWeight: '800', padding: '0.75rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  🔍 Search Judgments & Citations
                </Link>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span style={{ fontSize: '0.85rem', color: goldPrimary, fontWeight: 'bold', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                📚 Authoritative Law Reporting Journals & Legal Repositories
              </span>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', fontFamily: fontSerif, marginTop: '0.5rem', marginBottom: '1rem' }}>
                Law Journals & Reports Covered
              </h2>
              <p style={{ fontSize: '0.95rem', color: '#cbd5e1', maxWidth: '750px', margin: '0 auto', lineHeight: '1.6' }}>
                Elum Law Directory indexes full-text decisions, headnotes, and citations from all 10 major Pakistani law reporting journals published since 1947 to present.
              </p>
            </div>

            <div style={{ overflowX: 'auto', borderRadius: '12px', border: `1px solid ${borderThemeColor}`, boxShadow: '0 12px 30px rgba(0,0,0,0.5)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#1e293b', borderBottom: `2px solid ${goldPrimary}` }}>
                    <th style={{ padding: '1rem 1.25rem', color: goldPrimary, fontWeight: 'bold' }}>Journal Code</th>
                    <th style={{ padding: '1rem 1.25rem', color: 'white', fontWeight: 'bold' }}>Journal Name</th>
                    <th style={{ padding: '1rem 1.25rem', color: '#38bdf8', fontWeight: 'bold' }}>Historical Range</th>
                    <th style={{ padding: '1rem 1.25rem', color: '#e2e8f0', fontWeight: 'bold' }}>Primary Scope</th>
                    <th style={{ padding: '1rem 1.25rem', color: goldPrimary, textAlign: 'center' }}>Search Filter</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { code: 'PLD', name: 'All Pakistan Legal Decisions', range: '1947 – Present', scope: 'Apex Court & High Court judgments' },
                    { code: 'SCMR', name: 'Supreme Court Monthly Review', range: '1968 – Present', scope: 'Exclusive Supreme Court of Pakistan rulings' },
                    { code: 'CLC', name: 'Civil Law Cases', range: '1979 – Present', scope: 'Civil law matters across all High Courts' },
                    { code: 'YLR', name: 'Yearly Law Reporter', range: '1999 – Present', scope: 'Civil, criminal, and constitutional judgments' },
                    { code: 'MLD', name: 'Monthly Law Digest', range: '1988 – Present', scope: 'High Court and Tribunal decisions digest' },
                    { code: 'PTD', name: 'Pakistan Tax Decisions', range: '1959 – Present', scope: 'Income Tax, Sales Tax & Customs tribunals' },
                    { code: 'PLC / PLC(CS)', name: 'Pakistan Labour & Service Cases', range: '1960 – Present', scope: 'Labour courts & Federal/Provincial Service Tribunals' },
                    { code: 'CLD', name: 'Corporate Law Decisions', range: '2002 – Present', scope: 'Banking, Corporate, SECP & Commercial cases' },
                    { code: 'PCRLJ', name: 'Pakistan Criminal Law Journal', range: '1968 – Present', scope: 'Criminal jurisdiction, bail & trials' },
                    { code: 'GBLR', name: 'Gilgit-Baltistan Law Reports', range: '2010 – Present', scope: 'Gilgit-Baltistan Supreme Appellate Court' }
                  ].map((row, idx) => (
                    <tr key={row.code} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(15, 23, 42, 0.7)' : 'rgba(30, 41, 59, 0.7)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '0.9rem 1.25rem', color: goldPrimary, fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.95rem' }}>{row.code}</td>
                      <td style={{ padding: '0.9rem 1.25rem', color: 'white', fontWeight: '600' }}>{row.name}</td>
                      <td style={{ padding: '0.9rem 1.25rem', color: '#38bdf8', fontWeight: '500' }}>{row.range}</td>
                      <td style={{ padding: '0.9rem 1.25rem', color: '#cbd5e1' }}>{row.scope}</td>
                      <td style={{ padding: '0.9rem 1.25rem', textAlign: 'center' }}>
                        <Link 
                          to={`/cases/search?category=${encodeURIComponent(row.code.split(' / ')[0])}`} 
                          onClick={(e) => {
                            if (!user) {
                              e.preventDefault();
                              setShowAccessModal(true);
                            }
                          }}
                          style={{ backgroundColor: 'rgba(236,201,75,0.15)', color: goldPrimary, border: `1px solid ${goldMuted}`, padding: '0.35rem 0.8rem', borderRadius: '6px', textDecoration: 'none', fontSize: '0.78rem', fontWeight: 'bold' }}
                        >
                          Filter {row.code.split(' / ')[0]}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

      </main>

      {/* 7. GOLD-TRIMMED FOOTER */}
      <footer style={{ 
        padding: '4rem 2rem', 
        textAlign: 'center', 
        backgroundColor: navyDark, 
        color: '#94a3b8',
        borderTop: `4px solid ${goldPrimary}`
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h4 style={{ fontFamily: fontSerif, color: 'white', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {language === 'EN' ? 'ELUM LAW ASSOCIATES' : 'ایلم لا ایسوسی ایٹس'}
          </h4>
          <p style={{ fontSize: '0.8rem', color: goldPrimary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
            {language === 'EN' ? 'AI Powered Legal Services Platform' : 'اے آئی سے لیس قانونی سروسز پلیٹ فارم'}
          </p>
          <p style={{ fontSize: '0.85rem', maxWidth: '600px', margin: '0 auto 2rem', lineHeight: '1.6' }}>
            {language === 'EN' 
              ? 'Protecting client rights and accelerating judicial access across Pakistan through state-of-the-art secure tunnels and predictive logic.'
              : 'محفوظ ترین چینلز اور جدید ترین ٹیکنالوجی کے ذریعے سائلین کے حقوق کا تحفظ اور پاکستان میں تیز ترین عدالتی رسائی کا حصول۔'}
          </p>
          <div style={{ width: '100%', height: '1px', backgroundColor: '#334155', marginBottom: '2rem' }} />
          
          {/* SOCIAL MEDIA LINKS & OFFICE ADDRESS */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="https://www.facebook.com/share/1Bryh1M8AC/?mibextid=wwXIfr" target="_blank" rel="noreferrer" title="Facebook - Elum Law Associates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#1877F2', color: 'white', boxShadow: '0 4px 10px rgba(24,119,242,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
            </a>
            <a href="https://x.com/ElumLaw" target="_blank" rel="noreferrer" title="X (Twitter) - @ElumLaw" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#1DA1F2', color: 'white', boxShadow: '0 4px 10px rgba(29,161,242,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
            </a>
            <a href="https://www.linkedin.com/in/elum-law-associates-94633a428?trk=contact-info" target="_blank" rel="noreferrer" title="LinkedIn - Elum Law Associates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#0A66C2', color: 'white', boxShadow: '0 4px 10px rgba(10,102,194,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
            </a>
            <a href="https://www.youtube.com/@ElumLawAssociates" target="_blank" rel="noreferrer" title="YouTube - @ElumLawAssociates" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FF0000', color: 'white', boxShadow: '0 4px 10px rgba(255,0,0,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
            </a>
            <a href="https://wa.me/923339389948" target="_blank" rel="noreferrer" title="WhatsApp Direct: +923339389948" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#25D366', color: 'white', boxShadow: '0 4px 10px rgba(37,211,102,0.3)', transition: 'transform 0.2s' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </a>
          </div>

          <div style={{ textAlign: 'center', fontSize: '0.88rem', color: '#cbd5e1', lineHeight: '1.7', marginBottom: '1.5rem' }}>
            <p style={{ margin: '0 0 0.3rem 0', fontWeight: 'bold' }}>📍 Office Address: Elum Law Associates, C-1, Times Complex, M.A Jinnah Road Quetta, Pakistan.</p>
            <p style={{ margin: 0 }}>📧 Email: <strong style={{ color: goldPrimary }}>elumlawassociates@gmail.com</strong> | 📞 WhatsApp: <strong style={{ color: goldPrimary }}>+92 333 9389948</strong></p>
          </div>

          <p style={{ fontSize: '0.8rem' }}>&copy; 2026 Elum Law Associates Pakistan. All rights reserved. Registered under domain: elumlaw.com</p>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '2rem', fontSize: '0.8rem' }}>
            <Link to="/terms" style={{ color: '#94a3b8', textDecoration: 'none' }}>Terms & Conditions</Link>
            <Link to="/privacy" style={{ color: '#94a3b8', textDecoration: 'none' }}>Privacy Policy</Link>
          </div>
        </div>
      </footer>

      {/* ACCESS SECURED LIVE CHAT MODAL */}
      {showAccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: fontSans
        }}>
          <div style={{
            backgroundColor: cardBg,
            padding: '2.5rem',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '90%',
            border: `2px solid ${goldPrimary}`,
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(236,201,75,0.15)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 1.25rem',
              border: `1.5px solid ${goldPrimary}`
            }}>
              <ShieldCheck size={32} color={goldPrimary} />
            </div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: '800', color: goldPrimary, marginBottom: '0.65rem', lineHeight: '1.3' }}>
              Welcome to Pakistan's AI Powered Elum Legal Services & Law Directory
            </h3>
            <p style={{ fontSize: '0.92rem', color: textColor, lineHeight: '1.65', marginBottom: '1.75rem', fontWeight: '500' }}>
              {language === 'EN' 
                ? 'Authentication Required: Until successful login, internal AI features, law directories, drafting engines & portals remain locked. Please log in to your verified account or register to proceed.'
                : 'رسائی کی اجازت درکار ہے: کامیاب لاگ ان تک، مصنوعی ذہانت کی خصوصیات، قوانین کی ڈائریکٹری اور ڈرافٹنگ پورٹل بند ہیں۔ سائن ان یا نیا اکاؤنٹ رجسٹر کریں۔'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Link to="/login" style={{
                backgroundColor: goldPrimary,
                color: navyDark,
                padding: '0.85rem',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '900',
                fontSize: '0.98rem',
                display: 'block',
                boxShadow: '0 4px 15px rgba(236,201,75,0.3)'
              }}>
                🔑 {language === 'EN' ? 'Sign In / Log In to Portal' : 'پورٹل میں لاگ ان کریں'}
              </Link>
              <Link to="/register" style={{
                backgroundColor: 'transparent',
                color: titleColor,
                border: `1.5px solid ${borderThemeColor}`,
                padding: '0.85rem',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                display: 'block'
              }}>
                📝 {language === 'EN' ? 'Register Case Account' : 'نیا کیس اکاؤنٹ رجسٹر کریں'}
              </Link>
              <button 
                onClick={() => setShowAccessModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: textMutedColor,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  marginTop: '0.5rem',
                  fontWeight: 500
                }}
              >
                {language === 'EN' ? 'Cancel' : 'منسوخ کریں'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LAW DICTIONARY MODAL */}
      <BlacksLawDictionaryModal 
        isOpen={showDictionaryModal} 
        onClose={() => setShowDictionaryModal(false)} 
      />

      {/* FLOATING WINDOW WITHIN WINDOW LIVE CHAT SUITE */}
      {showLiveChatWindow && (
        <ChatSystem 
          isModalView={true} 
          onClose={() => setShowLiveChatWindow(false)} 
        />
      )}

      {/* FLOATING WINDOW WITHIN WINDOW ELUM LAW DIRECTORY */}
      {showLawDirectoryModal && (
        <LawDirectory 
          isModalView={true} 
          onClose={() => setShowLawDirectoryModal(false)} 
        />
      )}

      {/* FLOATING WINDOW WITHIN WINDOW SMART LEGAL DRAFTING & PLEADING */}
      {showSmartDraftingModal && (
        <SmartLegalDrafting 
          isModalView={true} 
          onClose={() => setShowSmartDraftingModal(false)} 
        />
      )}

      {/* FLOATING WINDOW WITHIN WINDOW AI LEGAL ASSISTANT */}
      {showAiAssistantModal && (
        <AiAssistant 
          isModalView={true} 
          onClose={() => setShowAiAssistantModal(false)} 
        />
      )}

      {/* FLOATING WINDOW WITHIN WINDOW ELUM MEET (ZOOM VIDEO CONFERENCE & PiP) */}
      <ElumVideoConferenceModal 
        isOpen={showVideoConferenceModal} 
        onClose={() => setShowVideoConferenceModal(false)} 
      />

      {/* INTERACTIVE MARQUEE 1 & 2 DETAIL MODAL (CASES & EVENTS) */}
      {activeMarqueeDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#0f172a',
            color: 'white',
            border: `2px solid ${goldPrimary}`,
            borderRadius: '16px',
            maxWidth: '750px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 60px rgba(0,0,0,0.85)',
            boxSizing: 'border-box'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.25rem 1.75rem',
              backgroundColor: '#1e293b',
              borderBottom: '1px solid rgba(236,201,75,0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  backgroundColor: activeMarqueeDetail.type === 'case' ? '#851414' : '#128c7e',
                  color: 'white',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {activeMarqueeDetail.type === 'case' ? '⚖️ Law Report Precedent' : '📸 Judicial Event Broadcast'}
                </span>
                {activeMarqueeDetail.citation && (
                  <span style={{ color: goldPrimary, fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {activeMarqueeDetail.citation}
                  </span>
                )}
              </div>
              <button 
                onClick={() => setActiveMarqueeDetail(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0.25rem' }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '2rem' }}>
              {/* Event Image (if present) */}
              {activeMarqueeDetail.imageUrl && (
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <img 
                    src={activeMarqueeDetail.imageUrl} 
                    alt={activeMarqueeDetail.title} 
                    style={{
                      maxHeight: '280px',
                      maxWidth: '100%',
                      borderRadius: '12px',
                      border: `2px solid ${goldPrimary}`,
                      objectFit: 'cover',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.5)'
                    }}
                  />
                </div>
              )}

              <h2 style={{ color: goldPrimary, fontSize: '1.4rem', fontWeight: '900', marginTop: 0, marginBottom: '0.5rem', lineHeight: '1.3' }}>
                {activeMarqueeDetail.title}
              </h2>

              {activeMarqueeDetail.subtitle && (
                <p style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: '600', marginBottom: '1.5rem', marginTop: 0 }}>
                  {activeMarqueeDetail.subtitle}
                </p>
              )}

              {activeMarqueeDetail.court && (
                <div style={{ fontStyle: 'italic', color: '#38bdf8', fontSize: '0.92rem', marginBottom: '1.25rem', paddingBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  Before: {activeMarqueeDetail.court}
                </div>
              )}

              {activeMarqueeDetail.type === 'case' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.75rem', fontSize: '0.95rem', lineHeight: '1.65' }}>
                  {activeMarqueeDetail.subject && (
                    <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.6)', padding: '1rem', borderRadius: '8px', borderLeft: `3px solid ${goldPrimary}` }}>
                      <strong style={{ color: goldPrimary, display: 'block', marginBottom: '0.25rem' }}>Subject:</strong>
                      <span style={{ color: '#e2e8f0' }}>{activeMarqueeDetail.subject}</span>
                    </div>
                  )}
                  {activeMarqueeDetail.held && (
                    <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.6)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid #10b981' }}>
                      <strong style={{ color: '#10b981', display: 'block', marginBottom: '0.25rem' }}>Held (Ratio Decidendi):</strong>
                      <span style={{ color: '#e2e8f0' }}>{activeMarqueeDetail.held}</span>
                    </div>
                  )}
                  {activeMarqueeDetail.summary && (
                    <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.6)', padding: '1rem', borderRadius: '8px', borderLeft: '3px solid #38bdf8' }}>
                      <strong style={{ color: '#38bdf8', display: 'block', marginBottom: '0.25rem' }}>Judgment Summary:</strong>
                      <span style={{ color: '#e2e8f0' }}>{activeMarqueeDetail.summary}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '1.75rem' }}>
                  <p style={{ color: '#e2e8f0', fontSize: '0.98rem', lineHeight: '1.7', marginBottom: '1.25rem' }}>
                    {activeMarqueeDetail.description}
                  </p>

                  {activeMarqueeDetail.highlights && activeMarqueeDetail.highlights.length > 0 && (
                    <div style={{ backgroundColor: 'rgba(30, 41, 59, 0.7)', padding: '1.25rem', borderRadius: '10px', border: '1px solid rgba(236, 201, 75, 0.2)', marginBottom: '1.5rem' }}>
                      <strong style={{ color: goldPrimary, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '0.75rem' }}>
                        Key Highlights & Directives:
                      </strong>
                      <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.8' }}>
                        {activeMarqueeDetail.highlights.map((h: string, i: number) => (
                          <li key={i}>{h}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Simulated Secure Courtroom Transmission Player */}
                  <div style={{
                    backgroundColor: '#000000',
                    borderRadius: '10px',
                    padding: '2rem 1.5rem',
                    textAlign: 'center',
                    border: '1px solid #334155',
                    marginBottom: '1rem'
                  }}>
                    <button
                      onClick={() => alert('▶ Streaming live courtroom session transmission buffer... Connected to secure judicial CDN.')}
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: goldPrimary,
                        color: '#0f172a',
                        fontSize: '1.5rem',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 0 20px rgba(236,201,75,0.6)'
                      }}
                    >
                      ▶
                    </button>
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                      Secure High Courtroom Video Transmission Clip
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const searchParam = activeMarqueeDetail.citation || activeMarqueeDetail.title;
                    setActiveMarqueeDetail(null);
                    navigate(`/law-directory?query=${encodeURIComponent(searchParam)}`);
                  }}
                  style={{
                    flex: 1,
                    minWidth: '220px',
                    backgroundColor: '#128c7e',
                    color: 'white',
                    padding: '0.85rem 1.5rem',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Search size={18} /> Search Case in Law Directory
                </button>

                {activeMarqueeDetail.linkUrl && activeMarqueeDetail.linkUrl !== '#' && (
                  <button
                    onClick={() => {
                      if (activeMarqueeDetail.linkUrl?.endsWith('.html')) {
                        window.open(activeMarqueeDetail.linkUrl, '_blank');
                      } else {
                        setActiveMarqueeDetail(null);
                        navigate(activeMarqueeDetail.linkUrl!);
                      }
                    }}
                    style={{
                      backgroundColor: 'rgba(236,201,75,0.15)',
                      color: goldPrimary,
                      border: `1px solid ${goldPrimary}`,
                      padding: '0.85rem 1.5rem',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <ExternalLink size={18} /> Open Standalone View
                  </button>
                )}

                <button
                  onClick={() => setActiveMarqueeDetail(null)}
                  style={{
                    backgroundColor: '#334155',
                    color: 'white',
                    padding: '0.85rem 1.5rem',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* DIRECT LANDING PAGE DOCUMENT DETAIL MODAL (FULL CASE / STATUTE SECTIONS INSPECTOR) */}
      {selectedLandingDoc && (
        <DocumentDetailModal
          document={selectedLandingDoc}
          onClose={() => setSelectedLandingDoc(null)}
          initialViewMode="brief"
        />
      )}
    </div>
  );
};

// FEATURE CARD WRAPPER
const FeatureCard: React.FC<{ 
  index: number, 
  activeHover: number | null, 
  onHover: (idx: number | null) => void, 
  themeCardBg: string,
  themeBorder: string,
  themeText: string,
  themeMuted: string,
  icon: React.ReactNode, 
  title: string, 
  description: string, 
  to: string,
  onClick?: (e: React.MouseEvent) => void
}> = ({ index, activeHover, onHover, themeCardBg, themeBorder, themeText, themeMuted, icon, title, description, to, onClick }) => {
  const isHovered = activeHover === index;
  return (
    <Link 
      to={to} 
      onClick={onClick}
      style={{ textDecoration: 'none', color: 'inherit' }} 
      onMouseEnter={() => onHover(index)} 
      onMouseLeave={() => onHover(null)}
    >
      <div style={{ 
        padding: '2.5rem 1.75rem 2rem', 
        backgroundColor: themeCardBg, 
        borderRadius: '16px', 
        textAlign: 'center', 
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.2)' : '0 4px 10px rgba(0,0,0,0.04)', 
        border: `2px solid ${isHovered ? '#ecc94b' : themeBorder}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
        cursor: 'pointer', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        transform: isHovered ? 'translateY(-6px)' : 'translateY(0)'
      }}>
        <div>
          <div style={{ 
            display: 'inline-flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            marginBottom: '1.5rem',
            color: isHovered ? '#ecc94b' : themeText,
            backgroundColor: isHovered ? 'rgba(236,201,75,0.18)' : 'rgba(255,255,255,0.08)',
            padding: '1.1rem',
            borderRadius: '50%',
            transition: 'all 0.3s ease'
          }}>
            {icon}
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '1rem', color: themeText, transition: '0.2s' }}>{title}</h3>
          <p style={{ color: themeMuted, lineHeight: '1.65', fontSize: '0.88rem' }}>{description}</p>
        </div>
        <div style={{
          marginTop: '1.5rem',
          fontSize: '0.8rem',
          fontWeight: '800',
          color: isHovered ? '#090d16' : '#ecc94b',
          backgroundColor: isHovered ? '#ecc94b' : 'rgba(236, 201, 75, 0.12)',
          border: '1px solid #ecc94b',
          padding: '0.45rem 1rem',
          borderRadius: '25px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          transition: 'all 0.3s ease'
        }}>
          <span>Launch Feature Portal</span>
          <span style={{ fontSize: '1rem' }}>↗</span>
        </div>
      </div>
    </Link>
  );
};

export default LandingPage;
